import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Paper, Typography, Grid, Box, CircularProgress, Button, makeStyles, ButtonGroup, Snackbar,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField
} from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import Alert from '@material-ui/lab/Alert';
import { Warning } from '@material-ui/icons';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';

// Import Child Components
import MapProviderControls from './MapProviderControls';
import DrawingToolbar from './DrawingToolbar';
import DrawingList from './DrawingList';
import DrawingEditor from './DrawingEditor';
import MapComponent from './MapComponent';
import Sidebar from './Sidebar';

// Import Restricted Area Components
import RestrictedAreaDialog from '../../HMI/RestrictedAreaDialog';

import {
    getPolygonArea,
    generateUniqueId, 

} from './utils';

// Import Utilities and Constants
import {
    loadEsriLeaflet, checkYearService, defaultStyle, getGeometryFromLayer,
    googleMapsProvider, googleSatelliteProvider, googleHybridProvider,
    enhancedGetPolygonArea,
} from './mapUtils';
import { AREA_TYPES } from './constants';

const defaultZoom = 15;
const defaultCenterCoords = [33.505979117258995, -112.15165210426117];

// Define a centralized theme with common values
const mapTheme = {
    colors: {
      primary: '#1976d2',
      secondary: '#f50057',
      success: '#4caf50',
      warning: '#ff9800',
      error: '#f44336',
      background: '#f5f5f5',
      border: '#e0e0e0',
    },
    borderRadius: 4,
};

const debugState = (message, stateToLog) => {
  if (process.env.NODE_ENV !== 'production') {
      console.group(`🐞 DEBUG: ${message}`);
      Object.entries(stateToLog).forEach(([key, value]) => {
          console.log(`${key}:`, value);
      });
      console.groupEnd();
  }
};

const debugGeometry = (message, layer, drawing) => {
  if (process.env.NODE_ENV !== 'production') {
    console.group(`🔍 GEOMETRY DEBUG: ${message}`);
    
    // Extract layer geometry if available
    let layerGeometry = null;
    if (layer) {
      try {
        if (layer.getLatLngs) {
          const latlngs = layer.getLatLngs();
          console.log('Layer latlngs:', latlngs);
          
          // If it's a nested array (polygon), get the first ring
          const points = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
          
          // Format points for better readability
          layerGeometry = points.map(ll => ({ lat: ll.lat.toFixed(6), lng: ll.lng.toFixed(6) }));
        } else {
          console.log('Layer does not have getLatLngs method');
        }
      } catch (error) {
        console.error('Error extracting geometry from layer:', error);
      }
    }
    
    // Extract drawing geometry
    let drawingGeometry = null;
    if (drawing?.geometry) {
      if (drawing.geometry.latlngs) {
        const points = Array.isArray(drawing.geometry.latlngs[0]) 
          ? drawing.geometry.latlngs[0] 
          : drawing.geometry.latlngs;
          
        drawingGeometry = points.map(ll => ({ lat: ll.lat.toFixed(6), lng: ll.lng.toFixed(6) }));
      } else {
        drawingGeometry = drawing.geometry;
      }
    }
    
    console.log('Layer Geometry:', layerGeometry);
    console.log('Drawing Geometry:', drawingGeometry);
    
    // Check if they match
    if (layerGeometry && drawingGeometry) {
      const geometriesMatch = JSON.stringify(layerGeometry) === JSON.stringify(drawingGeometry);
      console.log('Geometries match?', geometriesMatch ? '✅ YES' : '❌ NO');
    }
    
    console.groupEnd();
  }
};

// Styles
const useStyles = makeStyles((theme) => ({
    // Layout containers
    container: {
      padding: theme.spacing(3),
    },
    title: {
      marginBottom: theme.spacing(2),
    },
    contentWrapper: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    mapWrapper: {
      flexGrow: 1,
      width: '70%',
      position: 'relative',
    },
    sidebarContainer: {
      width: '28%',
      height: '70vh',
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
      borderRadius: mapTheme.borderRadius,
      overflowY: 'scroll',
    //   paddingBottom: 100
    },
    controlPanel: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: mapTheme.borderRadius,
    },
    drawingList: {
      maxHeight: 300,
      overflowY: 'auto',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: mapTheme.borderRadius,
    },
    colorSwatch: {
      width: 24,
      height: 24,
      border: '1px solid #ccc',
      borderRadius: '4px',
      display: 'inline-block',
      marginLeft: theme.spacing(1),
      cursor: 'pointer',
      verticalAlign: 'middle'
    },
    buttonGroup: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    buttonGroupColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    statusBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
    },
}));

// Simplified map styles for consistent rendering
const mapStyles = {
    default: {
      color: mapTheme.colors.primary,
      weight: 2,
      opacity: 0.7,
      fillColor: mapTheme.colors.primary,
      fillOpacity: 0.2,
    },
    selected: {
      color: mapTheme.colors.secondary,
      weight: 3,
      opacity: 0.8,
      fillColor: mapTheme.colors.secondary,
      fillOpacity: 0.3,
    },
    propertyLine: {
      color: mapTheme.colors.success,
      weight: 3,
      opacity: 0.8,
      fillColor: mapTheme.colors.success,
      fillOpacity: 0.1,
    },
    finalPolygon: {
      color: mapTheme.colors.secondary,
      weight: 3,
      opacity: 0.8,
      fillColor: mapTheme.colors.secondary,
      fillOpacity: 0.3,
    },
};

const DynamicMaricopaMap = (props) => {
    const classes = useStyles();
    const history = useHistory();
    const { jobsiteId } = useParams();
    // Component render
    // --- State declarations ---
    // Providers & Loading
    const [providers, setProviders] = useState([]);
    const [currentProvider, setCurrentProvider] = useState({});
    const [yearProvidersLoading, setYearProvidersLoading] = useState(true);
    const [initError, setInitError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });

    // Drawings & Selection
    // User create shapes (areas and shit)
    const [drawings, setDrawings] = useState([]);
    const [selectedDrawingId, setSelectedDrawingId] = useState(null);
    const [editingShape, setEditingShape] = useState(null);

    // Drawing Mode
    const [isDrawingModeActive, setIsDrawingModeActive] = useState(false);
    const [currentDrawTool, setCurrentDrawTool] = useState(null);
    const [drawingMode, setDrawingMode] = useState('polygon');
    const [isHybridMode, setIsHybridMode] = useState(false);
    
    // Map View
    const [currentZoom, setCurrentZoom] = useState(12);
    const [currentCenter, setCurrentCenter] = useState([33.55, -112.0]);
    const mapBoundsRef = useRef(null);
    
    // Job Site Data
    const [selectedProvider, setSelectedProvider] = useState({});
    // Areas loaded from api (Property lines etc)
    const [polygons, setPolygons] = useState([]); 
    const [selectedPolygonIndex, setSelectedPolygonIndex] = useState(null);
    const [jobSite, setJobSite] = useState(null);
    const [center, setCenter] = useState(defaultCenterCoords);
    const [zoom, setZoom] = useState(defaultZoom);
    const [activeTab, setActiveTab] = useState(0);
    const [propertyLines, setPropertyLines] = useState([])

    const [currentlyEditingId, setCurrentlyEditingId] = useState(null);
    const [geofenceRadius, setGeofenceRadius] = useState(200); // Default radius in meters
    const [geofenceCircle, setGeofenceCircle] = useState(null);
    const [geofenceCenter, setGeofenceCenter] = useState(null);
    const [isEditingGeofence, setIsEditingGeofence] = useState(false);
    const [manualPropertyLines, setManualPropertyLines] = useState([]);
    const [selectedPropertyLineId, setSelectedPropertyLineId] = useState(null);
    const [nextDrawingIsPropertyLine, setNextDrawingIsPropertyLine] = useState(false);

    // Restricted Areas State
    const [restrictedAreas, setRestrictedAreas] = useState([]);
    const [nextDrawingIsRestrictedArea, setNextDrawingIsRestrictedArea] = useState(false);
    const [tempRestrictedAreaPaths, setTempRestrictedAreaPaths] = useState([]);
    const [restrictedAreaDialogOpen, setRestrictedAreaDialogOpen] = useState(false);
    const [editingRestrictedArea, setEditingRestrictedArea] = useState(null);
    const [selectedRestrictedAreaId, setSelectedRestrictedAreaId] = useState(null);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, areaId: null, areaName: '' });
    const [invalidCoordsDialogOpen, setInvalidCoordsDialogOpen] = useState(false);
    const [manualLat, setManualLat] = useState('');
    const [manualLng, setManualLng] = useState('');
    const [savingCoords, setSavingCoords] = useState(false);
    const [showAPNInput, setShowAPNInput] = useState(false)
    
    // --- Refs ---
    const mapInstanceRef = useRef(null);
    const drawnItemsLayerRef = useRef(null);
    const drawControlInstanceRef = useRef(null);
    const jobSiteAreasLoaded = useRef(false);
    const propertyLinesFetched = useRef(false);
    const currentlyEditingIdRef = useRef(currentlyEditingId);
    const nextDrawingIsPropertyLineRef = useRef(false)
    const nextDrawingIsRestrictedAreaRef = useRef(false)
    const drawingsRef = useRef(null)
    const polygonsLayerRef = useRef(null)
    const polygonsRef = useRef(null)
    const editingShapeRef = useRef(null)

    useEffect(() => {
        currentlyEditingIdRef.current = currentlyEditingId;
    }, [currentlyEditingId]);


    useEffect(() => {
      console.log('300 editingShape -- ', editingShape)
      editingShapeRef.current = editingShape;
  }, [editingShape]);


    useEffect(() => {
      console.log('295 drawings updated...', drawings)
      drawingsRef.current = drawings
    }, [drawings])

    useEffect(() => {
      polygonsRef.current = polygons
    }, [polygons])

      // Add this helper function in DynamicMap.js
    const attachGeofenceToMap = useCallback(() => {
        if (!geofenceCircle || !mapInstanceRef.current) return;
        
        console.log('Manually attaching geofence circle to map');
        
        try {
        // First, remove if already on a map
        if (geofenceCircle._map) {
            geofenceCircle.remove();
        }
        
        // Add directly to map
        geofenceCircle.addTo(mapInstanceRef.current);
        
        // Also add to drawn items layer for consistency
        if (drawnItemsLayerRef.current) {
            // First check if already in this layer
            let alreadyInLayer = false;
            drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer === geofenceCircle) {
                alreadyInLayer = true;
            }
            });
            
            if (!alreadyInLayer) {
            drawnItemsLayerRef.current.addLayer(geofenceCircle);
            }
        }
        
        // Make sure circle is made interactive
        geofenceCircle.options.interactive = true;
        
        console.log('Geofence attached successfully:', 
            geofenceCircle._map ? 'ON MAP' : 'NOT ON MAP',
            'In layer group:', drawnItemsLayerRef.current?.hasLayer(geofenceCircle)
        );
        } catch (error) {
        console.error('Error attaching geofence circle:', error);
        }
    }, [geofenceCircle, mapInstanceRef, drawnItemsLayerRef]);

    const createDefaultGeofenceCircle = useCallback(() => {
      console.log('...createDefaultGeofenceCircle', jobSite)
        if (!jobSite || !mapInstanceRef.current) return null;
        let doIt = false
        if (doIt) return
        try {
          if (geofenceCircle && geofenceCircle._map) {
            geofenceCircle.remove();
          }
          if (drawnItemsLayerRef.current) {
            drawnItemsLayerRef.current.eachLayer(layer => {
              if (layer._id === 'geofence_circle' || layer.options.isGeofence) {
                drawnItemsLayerRef.current.removeLayer(layer);
              }
            });
          }
          // Get center coordinates from job site
          let centerCoords;
          if (jobSite.location && jobSite.location.coordinates && jobSite.location.coordinates.length === 2) {
              centerCoords = [jobSite.location.coordinates[1], jobSite.location.coordinates[0]];
          } else if (jobSite.latitude && jobSite.longitude) {
              centerCoords = [jobSite.latitude, jobSite.longitude];
          } else {
              centerCoords = currentCenter || [33.55, -112.0];
          }
          console.log('Get our radius??', jobSite)
          // Get radius from job site or use default
          const radius = jobSite.radius || 200;
          
          console.log(`Creating default geofence: Center: ${centerCoords}, Radius: ${radius}m`);
          
          // Create a new circle that's not connected to the draw control
          const circleStyle = {
              color: '#3388ff',
              weight: 3,
              opacity: 0.8,
              fillColor: '#3388ff',
              fillOpacity: 0.2
          };
          
          // Remove existing circle if present
          if (geofenceCircle && geofenceCircle._map) {
              geofenceCircle.remove();
          }
          
          // Create the circle and IMMEDIATELY add it to the map
          const circle = L.circle(centerCoords, {
              radius: radius,
              ...circleStyle
          }).addTo(mapInstanceRef.current);  // This is the key line!
          
          // Set a unique ID
          circle._id = 'geofence_' + uuidv4();
          
          // Make sure it's interactive and visible
          circle.options.interactive = true;
          
          // Add to drawnItems explicitly
          if (drawnItemsLayerRef.current) {
              drawnItemsLayerRef.current.addLayer(circle);
          }
          circle.options.isGeofence = true;
          circle._id = 'geofence_circle';
          // Make sure it's editable
          circle.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              if (circle.editing) {
              circle.editing.enable();
              }
          });
          
          // Update state
          setGeofenceCircle(circle);
          setGeofenceRadius(radius);
          setGeofenceCenter({
              lat: centerCoords[0],
              lng: centerCoords[1]
          });
          
          return circle;
        } catch (error) {
          console.error("Error creating default geofence circle:", error);
          return null;
        }
    }, [jobSite, mapInstanceRef, currentCenter, geofenceCircle, drawnItemsLayerRef]);

    useEffect(() => {
        if (jobSite && mapInstanceRef.current && !geofenceCircle) {
        console.log('Creating geofence circle on job site load');
        const circle = createDefaultGeofenceCircle();
        
        // Ensure the circle is attached to the map after creation
        if (circle) {
            setTimeout(() => {
            attachGeofenceToMap();
            }, 100); // Small timeout to ensure state updates
        }
        }
    }, [jobSite, mapInstanceRef, geofenceCircle, createDefaultGeofenceCircle, attachGeofenceToMap]);
      
    // Add this additional effect to ensure geofence stays attached
    useEffect(() => {
        if (geofenceCircle && mapInstanceRef.current && !geofenceCircle._map) {
        console.log('Reattaching disconnected geofence circle');
        attachGeofenceToMap();
        }
    }, [geofenceCircle, mapInstanceRef, attachGeofenceToMap]);

    // Add this useEffect to clean up editing when component unmounts
    useEffect(() => {
      return () => {
        // Disable any active editing operations
        if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer.editing && layer.editing.enabled()) {
              layer.editing.disable();
            }
          });
        }
        
        if (polygonsLayerRef.current) {
          polygonsLayerRef.current.eachLayer(layer => {
            if (layer.editing && layer.editing.enabled()) {
              layer.editing.disable();
            }
          });
        }
      };
    }, []);

    //   useEffect(() => {
    //     // After job site data is loaded and map is ready
    //     console.log('380...useEffect', jobSite, geofenceCircle)
    //     if (jobSite && mapInstanceRef.current && !geofenceCircle) {
    //       console.log('Creating geofence circle because job site loaded and no circle exists');
    //       const circle = createDefaultGeofenceCircle();
          
    //       // If successfully created, zoom to it
    //       if (circle && mapInstanceRef.current) {
    //         try {
    //           // Get the center
    //           const center = circle.getLatLng();
              
    //           // Set an appropriate zoom
    //           let zoomLevel = 15;
    //           const radius = circle.getRadius();
    //           if (radius < 100) zoomLevel = 18;
    //           else if (radius < 500) zoomLevel = 16;
    //           else if (radius < 2000) zoomLevel = 14;
    //           else zoomLevel = 12;
              
    //           // Set the view
    //           setTimeout(() => {
    //             mapInstanceRef.current.setView(center, zoomLevel, { animate: true });
    //           }, 500); // Small timeout to ensure map is ready
    //         } catch (error) {
    //           console.error('Error zooming to circle:', error);
    //         }
    //       }
    //     }
    //   }, [jobSite, mapInstanceRef, geofenceCircle, createDefaultGeofenceCircle]);
      

    // --- Utility functions ---
    const showLoading = useCallback((message = 'Loading...') => {
        setLoading(true);
        setLoadingMessage(message);
    }, []);

    const hideLoading = useCallback(() => {
      console.log('Hide loading called...')
        setLoading(false);
        setLoadingMessage('');
    }, []);

    const showSnackbar = useCallback((message, type = 'success') => {
        setSnackbar({ open: true, message, type });
    }, []);

    const hideSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // Function to ensure vertex styling is applied
    const ensureVertexStyling = useCallback(() => {
        // This ensures the CSS is applied for edit vertices
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
            .leaflet-vertex-icon {
                border-radius: 50%;
                border: 2px solid #1976d2;
                background-color: white;
                width: 10px !important;
                height: 10px !important;
                margin-left: -5px !important;
                margin-top: -5px !important;
            }
            .leaflet-midpoint-icon {
                border-radius: 50%;
                border: 2px solid #1976d2;
                background-color: white;
                opacity: 0.7;
                width: 8px !important;
                height: 8px !important;
                margin-left: -4px !important;
                margin-top: -4px !important;
            }
            
            /* Hide the edit guide line that shows the polygon outline */
            .leaflet-editing-icon {
                opacity: 1 !important;
            }
            .leaflet-edit-move {
                display: none !important;
            }
            .leaflet-edit-resize {
                display: none !important;
            }
            
            /* This is the key class to hide the guides */
            .leaflet-edit-line-guide {
                opacity: 0 !important;
                display: none !important;
            }
        `;
        
        // Only add if not already present
        if (!document.querySelector('style[data-vertex-styling="true"]')) {
            style.setAttribute('data-vertex-styling', 'true');
            document.getElementsByTagName('head')[0].appendChild(style);
        }
    }, []);

    // Function to update a polygon in the polygons state array
    const updatePolygon = useCallback((index, updatedProperties) => {
      console.log('...updatePolygon', index, updatedProperties)
      const newPoly = {
        ...polygons[index],
        ...updatedProperties
      }
      console.log('polygons', polygons)
      console.log('drawn...', drawingsRef)
      console.log('New Polygon:::', newPoly)
      console.log('Set setEditingShape...', newPoly)
      setEditingShape(newPoly)
        setPolygons(prevPolygons => {
            // Create a new array to ensure React detects the change
            const newPolygons = prevPolygons.map((polygon, i) => {
                if (i === index) {
                    console.log(`Updating polygon at index ${index}:`, updatedProperties);
                    return { ...polygon, ...updatedProperties };
                }
                return polygon;
            });
            console.log('New Polygons!!', newPolygons)
            return newPolygons;
        });
    }, [setPolygons, setEditingShape]);
    
    // Function to handle polygon edits
const handlePolygonEdited = useCallback((polygonId, newPaths) => {
    if (!polygonId) return;
    
    console.log(`Polygon ${polygonId} was edited:`, newPaths);
    console.log('polygons', polygons)
    console.log('drawings', drawings)
    console.log('DrawnItemms', drawnItemsLayerRef)
    console.log('Editing Shape...', editingShape)
    console.log('drawingsRef...drawingsRef', drawingsRef)
    // Find the polygon in our state by ID
    // const index = drawingsRef?.current?.findIndex(p => p.id === polygonId);
    
    // if (index !== -1) {
    //   // Instead of immediately updating state, only do it when editing is complete
    //   if (newPaths && newPaths.length > 0) {
    //     updatePolygon(index, { paths: newPaths });
    //     console.log(`Updated polygon at index ${index} with new paths`);
    //   }
    // } else {
    //   console.warn(`Could not find polygon with ID ${polygonId} to update`);
    // }
  }, [polygons, updatePolygon, drawnItemsLayerRef, drawings, editingShape, drawingsRef]);
    
    // Function to delete a polygon
    const deletePolygon = useCallback((indexToDelete) => {
        setPolygons(prevPolygons => {
            const polygonToRemove = prevPolygons[indexToDelete];
            console.log(`Deleting polygon at index ${indexToDelete}:`, polygonToRemove);
            const newPolygons = prevPolygons.filter((_, index) => index !== indexToDelete);

            // Adjust selected polygon index if necessary
            if (selectedPolygonIndex === indexToDelete) {
                setSelectedPolygonIndex(null); // Deselect if deleted
            } else if (selectedPolygonIndex !== null && selectedPolygonIndex > indexToDelete) {
                setSelectedPolygonIndex(prev => prev - 1); // Adjust index if item before it was deleted
            }
            return newPolygons;
        });
    }, [selectedPolygonIndex]);
    
    // Function to toggle polygon visibility
    const toggleVisibility = useCallback((index) => {
        const polygon = polygons[index];
        if (polygon) {
            console.log(`Toggling visibility for polygon index ${index}`);
            updatePolygon(index, { isHidden: !polygon.isHidden });
        } else {
            console.warn(`toggleVisibility: Polygon at index ${index} not found.`);
        }
    }, [polygons, updatePolygon]);


    
    // Function to calculate net area (accounting for subtractions)
    const calculateNetArea = useCallback((mainAreaPolygon) => {
        if (!mainAreaPolygon || !mainAreaPolygon.paths) return 0;
        
        const mainAreaInfo = getPolygonArea(mainAreaPolygon.paths);
        const mainAreaFeet = mainAreaInfo?.areaFeet ?? 0;
        
        // Find subtraction polygons associated with this main area
        const subtractions = polygons.filter(p =>
            p.type === 'subtraction' &&
            p.parentArea === mainAreaPolygon.id
        );
        
        const totalSubtractedFeet = subtractions.reduce((sum, subPolygon) => {
            const subAreaInfo = getPolygonArea(subPolygon.paths);
            return sum + (subAreaInfo?.areaFeet ?? 0);
        }, 0);
        
        return mainAreaFeet - totalSubtractedFeet;
    }, [polygons]);

    // --- Map callbacks ---
    // Handle map initialization
    const handleMapReady = useCallback(({ map, drawnItemsLayer, drawControlInstance, polygonsLayer }) => {
        console.log("DynamicMaricopaMap: Map is ready");
        mapInstanceRef.current = map;
        drawnItemsLayerRef.current = drawnItemsLayer;
        drawControlInstanceRef.current = drawControlInstance;
        polygonsLayerRef.current = polygonsLayer; 
        
        // Apply vertex styling to ensure only dots show when editing
        ensureVertexStyling();
        
        // Try to attach the geofence if it exists
        if (geofenceCircle) {
          attachGeofenceToMap();
        }
      }, [ensureVertexStyling, geofenceCircle, attachGeofenceToMap]);

    // Handle map view changes
    const handleMapViewChange = useCallback(({ center, zoom, bounds }) => {
        console.log("Map View Change:", center, zoom);
        
        // Update our state to track the current view
        setCurrentCenter([center.lat, center.lng]);
        setCurrentZoom(zoom);
        
        // Save bounds to session storage for later restoration
        try {
            const dataToStore = {
                center: center,
                zoom: zoom,
                bounds: {
                    north: bounds.getNorth(), 
                    south: bounds.getSouth(),
                    east: bounds.getEast(), 
                    west: bounds.getWest(),
                }
            };
            sessionStorage.setItem('maricopa-map-view', JSON.stringify(dataToStore));
        } catch (e) { 
            console.error('Error saving map view:', e); 
        }
    }, []);

    // Drawing callbacks
    const handleDrawStart = useCallback((event) => {
        // Determine the tool type
        const tool = event.layerType || 
            (event.type?.includes('edit') ? 'edit' : 
            (event.type?.includes('delete') ? 'delete' : currentDrawTool));
        
        console.log('Parent: Draw Start - Tool:', tool);

        setIsDrawingModeActive(true);
        setCurrentDrawTool(tool);
        setSelectedDrawingId(null);
        console.log('Set Editing Shape  setEditingShape 667')
        setEditingShape(null);
    }, [currentDrawTool]);

    const handleDrawStop = useCallback((event) => {
      console.log('735 handle Draw Stop....', event)
        const stoppedTool = event?.layerType || currentDrawTool;
        console.log('Parent: Draw Stop - Tool:', stoppedTool);

        // Reset drawing state
        setIsDrawingModeActive(false);
        setCurrentDrawTool(null);
    }, [currentDrawTool]);

    const handleStartGeofenceDrawing = useCallback(() => {
        console.log('handleStartGeofenceDrawing called');
        setIsEditingGeofence(true);
        const existingGeofence = polygons.find(p => p.type === 'geofence');
  
        if (existingGeofence) {
          console.log('ExistingGeofence exists....765', existingGeofence)
          // If we have one, just select it for editing
          setSelectedPolygonIndex(polygons.indexOf(existingGeofence));
          setCurrentlyEditingId(existingGeofence.id);
          
          // Center map on it
          if (mapInstanceRef.current) {
            const centerPos = [existingGeofence.center.lat, existingGeofence.center.lng];
            // mapInstanceRef.current.setView(centerPos, 16);
          }
          
          // If we have a circle reference, enable editing
          if (geofenceCircle && geofenceCircle.editing) {
            geofenceCircle.editing.enable();
          }
          
          showSnackbar('Edit the geofence by dragging the circle or its edge', 'info');
          return;
        }

        // Check if we already have a geofence circle
        if (geofenceCircle) {
          console.log('Existing geofence found - enabling editing', geofenceCircle);
          
          // Make sure it's on the map
          if (!geofenceCircle._map && mapInstanceRef.current) {
            geofenceCircle.addTo(mapInstanceRef.current);
          }
          
          // Center map on it
          if (mapInstanceRef.current) {
            const centerPos = geofenceCircle.getLatLng();
            // mapInstanceRef.current.setView(centerPos, 16);
          }
          
          // Explicitly enable editing
          setTimeout(() => {
            if (geofenceCircle.editing) {
              geofenceCircle.editing.enable();
              
              // Make sure edit handles are visible by adding this class
              if (geofenceCircle._path) {
                L.DomUtil.addClass(geofenceCircle._path, 'leaflet-editing-circle');
              }
              
              // Make handles visible by directly showing them
              document.querySelectorAll('.leaflet-edit-resize, .leaflet-edit-move')
                .forEach(el => el.style.display = 'block');
            }
          }, 100);
          
          showSnackbar('Geofence editing enabled - drag circle or its edge to resize', 'info');
          return;
        }

        // Calculate center position
        let centerPos;
        if (jobSite?.location?.coordinates?.length === 2) {
          centerPos = {
            lat: jobSite.location.coordinates[1], 
            lng: jobSite.location.coordinates[0]
          };
        } else if (jobSite?.latitude && jobSite?.longitude) {
          centerPos = {
            lat: jobSite.latitude,
            lng: jobSite.longitude
          };
        } else {
          centerPos = {
            lat: currentCenter[0],
            lng: currentCenter[1]
          };
        }
        
        // Set radius from job site if available, or use default
        const radius = jobSite?.radius || 200;
        console.log('839...radius', jobSite)
        // Create a geofence polygon object that will render as a circle
        const newGeofence = {
          id: uuidv4(),
          name: 'Geofence Circle',
          type: 'geofence',
          center: centerPos,
          radius: radius,
          fillColor: '#3388ff',
          strokeColor: '#3388ff',
          // Add any other properties needed for rendering
        };
        
        // Add to polygons array
        setPolygons(prev => [...prev, newGeofence]);
        
        // Update state
        setGeofenceRadius(radius);
        setGeofenceCenter(centerPos);
        
        // Select it for editing
        setTimeout(() => {
          const idx = polygons.length; // It will be the last one added
          setSelectedPolygonIndex(idx);
          setCurrentlyEditingId(newGeofence.id);
        }, 100);
        
        showSnackbar('Geofence created - you can now edit it', 'success');
    }, [geofenceCircle, mapInstanceRef, showSnackbar, createDefaultGeofenceCircle]);


    const handleGeofenceRadiusChange = useCallback((newRadius) => {
        console.log('Geofence radius changed:', newRadius);
        setGeofenceRadius(newRadius);
        
        // Update the geofence in polygons array
        setPolygons(prev => prev.map(p => {
        if (p.type === 'geofence') {
            return { ...p, radius: newRadius };
        }
        return p;
        }));
    }, []);
    
    const handleGeofenceMoved = useCallback((newCenter) => {
        console.log('Geofence moved to:', newCenter);
        const centerObj = {
        lat: newCenter.lat,
        lng: newCenter.lng
        };
        setGeofenceCenter(centerObj);
        
        // Update the geofence in polygons array
        setPolygons(prev => prev.map(p => {
        if (p.type === 'geofence') {
            return { ...p, center: centerObj };
        }
        return p;
        }));
    }, []);


      // Function to update geofence radius from text input - modify existing function or add if missing
      const handleUpdateGeofenceRadius = useCallback((e) => {
        const newRadius = parseFloat(e.target.value);
        if (!isNaN(newRadius) && newRadius > 0) {
          setGeofenceRadius(newRadius);
          
          // If we have a reference to the circle layer, update its radius
          if (geofenceCircle && geofenceCircle.setRadius) {
            geofenceCircle.setRadius(newRadius);
          }
        }
      }, [geofenceCircle]);
      const handleSaveGeofenceArea = useCallback(async () => {
        if (!jobSite?._id) {
          showSnackbar('Cannot save, no job site loaded.', 'error');
          return;
        }
        
        if (!geofenceRadius || geofenceRadius <= 0) {
          showSnackbar('Invalid geofence radius.', 'error');
          return;
        }
        
        try {
          showLoading('Saving geofence...');
          
          // Get the current center and radius from the circle if it exists
          let centerToSave;
          let radiusToSave = geofenceRadius;
          
          if (geofenceCircle && geofenceCircle._map) {
            // Get center directly from the Leaflet circle
            const circleCenter = geofenceCircle.getLatLng();
            centerToSave = {
              lat: circleCenter.lat,
              lng: circleCenter.lng
            };
            
            // Get radius directly from the circle
            radiusToSave = geofenceCircle.getRadius();
          } else {
            // Fallback to stored values
            centerToSave = geofenceCenter || {
              lat: center[0],
              lng: center[1]
            };
          }
          
          console.log('Saving geofence with center:', centerToSave, 'radius:', radiusToSave);
          
          // Prepare the data to save
          const geofenceData = {
            jobSiteId: jobSite._id,
            radius: radiusToSave,
            center: centerToSave
          };
          
          // Turn off editing mode
          if (geofenceCircle && geofenceCircle.editing) {
            geofenceCircle.editing.disable();
          }
          setIsEditingGeofence(false);
          
          // Send the request to your API
          const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, geofenceData, {
            params: {
              type: 'SAVE_GEOFENCE'
            }
          });
          console.log('Save geofence response....', response)
          
          // Update local state to reflect saved changes
          setGeofenceCenter(centerToSave);
          setGeofenceRadius(radiusToSave);
          
          // Update the jobsite object with new location
          if (jobSite) {
            const updatedJobSite = {
              ...jobSite,
              geofenceRadius: radiusToSave,
              location: {
                type: "Point",
                coordinates: [centerToSave.lng, centerToSave.lat]  // GeoJSON format is [lng, lat]
              }
            };
            setJobSite(updatedJobSite);
          }
          
          showSnackbar('Geofence location and radius saved successfully', 'success');
          props.notification({
            type: 'success',
            title: 'Geofence Saved',
            message: 'Jobsite location and service radius updated'
          });
          
          return response.data;
        } catch (error) {
          console.error('Error saving geofence:', error.response?.data || error.message);
          showSnackbar(`Failed to save geofence: ${error.response?.data?.message || 'Server error'}`, 'error');
          props.notification({
            type: 'error',
            title: 'Error',
            message: 'Failed to save geofence'
          });
          
          return null;
        } finally {
          hideLoading();
        }
      }, [
        jobSite, 
        geofenceRadius, 
        geofenceCircle,
        geofenceCenter,
        center, 
        props.url, 
        props.notification, 
        showLoading, 
        hideLoading, 
        showSnackbar
      ]);

      const handleStartPropertyLineDrawing = useCallback(() => {
        if (isDrawingModeActive) return;
        
        console.log("Starting property line drawing mode");
        
        // Set drawing mode to polygon
        setDrawingMode('polygon');
        
        // Activate the polygon tool
        if (drawControlInstanceRef.current) {
          const polygonHandler = drawControlInstanceRef.current._toolbars.draw._modes.polygon.handler;
          if (polygonHandler) {
            console.log("Enabling polygon handler for property line");
            polygonHandler.enable();
          } else {
            console.warn("Could not find polygon handler in draw control");
          }
        } else {
          console.warn("Draw control reference not available");
        }
        
        // Reset polygon style to default green for property lines
        if (drawControlInstanceRef.current && drawControlInstanceRef.current.options.draw.polygon) {
          drawControlInstanceRef.current.options.draw.polygon.shapeOptions = {
            color: '#4caf50',
            weight: 3,
            opacity: 0.8,
            fillColor: '#4caf50',
            fillOpacity: 0.1
          };
        }

        // Set flag to mark next drawn polygon as a property line
        setNextDrawingIsPropertyLine(true);
        nextDrawingIsPropertyLineRef.current = true

        showSnackbar('Draw a property boundary by clicking on the map', 'info');
      }, [isDrawingModeActive, drawControlInstanceRef, showSnackbar]);

      // ==================== RESTRICTED AREA HANDLERS ====================

      const handleStartRestrictedAreaDrawing = useCallback(() => {
        if (isDrawingModeActive) return;

        console.log("Starting restricted area drawing mode");

        // Set drawing mode to polygon
        setDrawingMode('polygon');

        // Update the drawing style for restricted areas (red color)
        if (drawControlInstanceRef.current) {
          // Update the polygon drawing options to use red color
          if (drawControlInstanceRef.current.options.draw.polygon) {
            drawControlInstanceRef.current.options.draw.polygon.shapeOptions = {
              fillColor: 'rgba(255, 0, 0, 0.35)',
              color: '#FF0000',
              weight: 3,
              fillOpacity: 0.35,
              opacity: 1
            };
          }

          // Also update the handler if it exists
          const polygonHandler = drawControlInstanceRef.current._toolbars.draw._modes.polygon.handler;
          if (polygonHandler) {
            // Update the handler's options
            if (polygonHandler.options) {
              polygonHandler.options.shapeOptions = {
                fillColor: 'rgba(255, 0, 0, 0.35)',
                color: '#FF0000',
                weight: 3,
                fillOpacity: 0.35,
                opacity: 1
              };
            }

            console.log("Enabling polygon drawing tool for restricted area with red style");
            setIsDrawingModeActive(true);
            setCurrentDrawTool('polygon');
            polygonHandler.enable();
          } else {
            console.warn("Polygon handler not available for restricted area");
          }
        } else {
          console.warn("Draw control reference not available for restricted area");
        }

        // Set flag to mark next drawn polygon as a restricted area
        setNextDrawingIsRestrictedArea(true);
        nextDrawingIsRestrictedAreaRef.current = true;
        console.log("Set nextDrawingIsRestrictedAreaRef to true");
        showSnackbar('Draw the restricted area boundary by clicking on the map', 'info');
      }, [isDrawingModeActive, drawControlInstanceRef, showSnackbar]);

      const handleRestrictedAreaDrawn = useCallback((layer) => {
        console.log("handleRestrictedAreaDrawn called with layer:", layer);
        if (!layer || !layer.getLatLngs) {
          console.error("Invalid layer passed to handleRestrictedAreaDrawn");
          return;
        }

        try {
          // Get the polygon paths first
          const latlngs = layer.getLatLngs();
          const paths = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
          const formattedPaths = paths.map(ll => ({ lat: ll.lat, lng: ll.lng }));

          // Create a unique ID for this restricted area
          const restrictedAreaId = generateUniqueId();

          // Tag the layer so we can find it later
          layer._restrictedAreaId = restrictedAreaId;
          layer._isTemporaryRestrictedArea = true;

          // Style the layer as red
          if (layer.setStyle) {
            layer.setStyle({
              fillColor: 'rgba(255, 0, 0, 0.35)',
              color: '#FF0000',
              weight: 3,
              fillOpacity: 0.35,
              opacity: 1
            });
          }

          // Make sure the layer is added to drawnItems if not already there
          if (drawnItemsLayerRef.current) {
            // Check if it's already added
            if (!drawnItemsLayerRef.current.hasLayer(layer)) {
              console.log("Adding restricted area layer to drawnItems");
              drawnItemsLayerRef.current.addLayer(layer);
            }

            // Ensure it's on the map
            if (!layer._map && mapInstanceRef.current) {
              console.log("Adding restricted area layer to map");
              layer.addTo(mapInstanceRef.current);
            }
          }

          console.log("Opening restricted area dialog with paths:", formattedPaths);

          // Store the paths and ID temporarily for the dialog
          setTempRestrictedAreaPaths(formattedPaths);
          setRestrictedAreaDialogOpen(true);

          showSnackbar('Please enter details for the restricted area', 'info');
        } catch (error) {
          console.error('Error processing restricted area:', error);
          showSnackbar('Failed to create restricted area', 'error');
        }
      }, [showSnackbar, generateUniqueId, drawnItemsLayerRef, mapInstanceRef]);

      const handleSaveRestrictedArea = useCallback(async (areaData) => {
        const jobSiteId = jobsiteId || jobSite?._id;

        console.log('=== handleSaveRestrictedArea START ===');
        console.log('Area data received:', areaData);
        console.log('Temp paths:', tempRestrictedAreaPaths);
        console.log('JobSite ID:', jobSiteId);
        console.log('Editing area:', editingRestrictedArea);

        if (!jobSiteId) {
          showSnackbar('No jobsite selected', 'error');
          return;
        }

        try {
          let updatedAreas;

          if (editingRestrictedArea?._id) {
            // Update existing area
            const updatedArea = {
              ...editingRestrictedArea,
              ...areaData,
              paths: tempRestrictedAreaPaths,
              type: 'restricted',
              fillColor: 'rgba(255, 0, 0, 0.35)',
              strokeColor: '#FF0000',
              jobSite: jobSiteId,
              isHidden: editingRestrictedArea.isHidden || false  // Preserve hidden state
            };

            updatedAreas = restrictedAreas.map(area =>
              area._id === editingRestrictedArea._id ? updatedArea : area
            );

            console.log('Updating existing area:', updatedArea);
          } else {
            // Create new area
            const newArea = {
              ...areaData,
              paths: tempRestrictedAreaPaths,
              type: 'restricted',
              fillColor: 'rgba(255, 0, 0, 0.35)',
              strokeColor: '#FF0000',
              jobSite: jobSiteId,
              isHidden: false  // Explicitly set isHidden to false for new areas
            };

            updatedAreas = [...restrictedAreas, newArea];
            console.log('Creating new area:', newArea);
          }

          // Save to backend
          const response = await axios.post(`${props.url}/api/customer/restrictedAreas`, {
            jobSiteId,
            restrictedAreas: updatedAreas
          });

          console.log('Backend response:', response.data);

          if (response.data.success) {
            setRestrictedAreas(response.data.restrictedAreas);
            console.log('Updated restrictedAreas state:', response.data.restrictedAreas);

            // Mark the layer as saved so it won't be removed
            if (drawnItemsLayerRef.current) {
              console.log('Marking layers as saved...');
              let foundLayer = false;
              drawnItemsLayerRef.current.eachLayer((layer) => {
                console.log('Checking layer:', {
                  isTemp: layer._isTemporaryRestrictedArea,
                  isSaved: layer._saved,
                  layerId: layer._leaflet_id
                });
                if (layer._isTemporaryRestrictedArea && !layer._saved) {
                  layer._saved = true;
                  layer._isTemporaryRestrictedArea = false;
                  foundLayer = true;
                  console.log("Marked restricted area layer as saved", layer._leaflet_id);
                }
              });
              if (!foundLayer) {
                console.warn('No temporary restricted area layer found to mark as saved!');
              }
            } else {
              console.warn('drawnItemsLayerRef.current is null!');
            }

            // Add to drawings for display with correct geometry format
            const drawing = {
              id: generateUniqueId(),
              type: 'polygon',
              name: areaData.name || 'Restricted Area',
              areaType: 'restricted',
              geometry: {
                type: 'polygon',
                latlngs: tempRestrictedAreaPaths
              },
              style: {
                fillColor: 'rgba(255, 0, 0, 0.35)',
                fillOpacity: 0.35,
                color: '#FF0000',
                weight: 3,
                opacity: 1
              },
              properties: {
                ...areaData,
                isRestrictedArea: true
              }
            };

            console.log('Adding drawing to state:', drawing);
            setDrawings(prev => {
              const updated = [...prev, drawing];
              console.log('Updated drawings array:', updated);
              return updated;
            });

            showSnackbar('Restricted area saved successfully', 'success');
            console.log('=== handleSaveRestrictedArea END SUCCESS ===');
          }
        } catch (error) {
          console.error('Error saving restricted area:', error);
          showSnackbar('Failed to save restricted area', 'error');
        } finally {
          // Don't clear these immediately - wait a bit for the layer to be properly saved
          setTimeout(() => {
            setTempRestrictedAreaPaths([]);
            setRestrictedAreaDialogOpen(false);
            setNextDrawingIsRestrictedArea(false);
            setEditingRestrictedArea(null);  // Clear editing state
          }, 100);
        }
      }, [tempRestrictedAreaPaths, jobsiteId, jobSite, restrictedAreas, props.url, showSnackbar, editingRestrictedArea, generateUniqueId]);

      // Handle editing a restricted area
      const handleEditRestrictedArea = useCallback((area) => {
        setEditingRestrictedArea(area);
        setTempRestrictedAreaPaths(area.paths || []);
        setRestrictedAreaDialogOpen(true);
      }, []);

      // Handle opening delete confirmation dialog
      const handleDeleteRestrictedArea = useCallback((areaId) => {
        const area = restrictedAreas.find(a => a._id === areaId);
        if (area) {
          setDeleteConfirmDialog({
            open: true,
            areaId: areaId,
            areaName: area.name || 'Unnamed Restricted Area'
          });
        }
      }, [restrictedAreas]);

      // Handle confirmed deletion
      const handleConfirmDeleteRestrictedArea = useCallback(async () => {
        const jobSiteId = jobsiteId || jobSite?._id;
        const areaId = deleteConfirmDialog.areaId;

        if (!jobSiteId || !areaId) {
          showSnackbar('Unable to delete area', 'error');
          setDeleteConfirmDialog({ open: false, areaId: null, areaName: '' });
          return;
        }

        try {
          const response = await axios.delete(`${props.url}/api/customer/restrictedAreas/${areaId}`);
          if (response.data.success) {
            setRestrictedAreas(prev => prev.filter(area => area._id !== areaId));

            // Also remove from drawings
            setDrawings(prev => prev.filter(d => d.id !== areaId));

            // Remove from map if it exists
            if (drawnItemsLayerRef.current) {
              drawnItemsLayerRef.current.eachLayer((layer) => {
                if (layer._restrictedAreaId === areaId) {
                  drawnItemsLayerRef.current.removeLayer(layer);
                }
              });
            }

            showSnackbar('Restricted area deleted successfully', 'success');
          } else {
            showSnackbar('Failed to delete restricted area', 'error');
          }
        } catch (error) {
          console.error('Error deleting restricted area:', error);
          showSnackbar('Error deleting restricted area', 'error');
        } finally {
          setDeleteConfirmDialog({ open: false, areaId: null, areaName: '' });
        }
      }, [deleteConfirmDialog.areaId, jobsiteId, jobSite, props.url, showSnackbar]);

      // Handle toggling restricted area visibility
      const handleToggleRestrictedAreaVisibility = useCallback((areaId) => {
        console.log('Toggling visibility for restricted area:', areaId);

        // Find the current state of the area
        const currentArea = restrictedAreas.find(a => a._id === areaId);
        const willBeHidden = currentArea ? !currentArea.isHidden : false;

        console.log('Current area:', currentArea, 'Will be hidden:', willBeHidden);

        // Update the restricted areas state
        setRestrictedAreas(prev => prev.map(area =>
          area._id === areaId ? { ...area, isHidden: willBeHidden } : area
        ));

        // Update the drawings array to match
        setDrawings(prev => prev.map(drawing => {
          // Check multiple ways the drawing might be associated with this restricted area
          const isMatchingArea = drawing.id === areaId ||
                                drawing.properties?._id === areaId ||
                                drawing.properties?.restrictedAreaId === areaId ||
                                (drawing.areaType === 'restricted' && drawing.name === currentArea?.name);

          if (isMatchingArea) {
            console.log('Found matching drawing to toggle:', drawing);
            // Update the drawing's hidden state and style
            return {
              ...drawing,
              isHidden: willBeHidden,
              style: {
                ...drawing.style,
                fillColor: willBeHidden ? 'transparent' : 'rgba(255, 0, 0, 0.35)',
                fillOpacity: willBeHidden ? 0 : 0.35,
                color: willBeHidden ? 'transparent' : '#FF0000',
                opacity: willBeHidden ? 0 : 1,
                weight: willBeHidden ? 0 : 3
              }
            };
          }
          return drawing;
        }));

        // Also update the actual map layers
        if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer._restrictedAreaId === areaId || layer._drawingId === areaId) {
              if (willBeHidden) {
                layer.setStyle({
                  fillOpacity: 0,
                  opacity: 0,
                  weight: 0
                });
              } else {
                layer.setStyle({
                  fillColor: 'rgba(255, 0, 0, 0.35)',
                  fillOpacity: 0.35,
                  color: '#FF0000',
                  opacity: 1,
                  weight: 3
                });
              }
            }
          });
        }
      }, [restrictedAreas, drawnItemsLayerRef]);

      // Handle selecting a restricted area
      const handleSelectRestrictedArea = useCallback((areaId) => {
        setSelectedRestrictedAreaId(areaId);
        // You could also center the map on this area if desired
        const area = restrictedAreas.find(a => a._id === areaId);
        if (area && area.paths && area.paths.length > 0) {
          const firstPoint = area.paths[0];
          setCenter([firstPoint.lat, firstPoint.lng]);
        }
      }, [restrictedAreas]);

      const fetchRestrictedAreas = useCallback(async (jobSiteId) => {
        if (!jobSiteId) return;

        try {
          const response = await axios.get(
            `${props.url}/api/customer/restrictedAreas?jobSiteId=${jobSiteId}`
          );

          if (response.data.success && response.data.restrictedAreas) {
            setRestrictedAreas(response.data.restrictedAreas);

            // Add to drawings for display with correct geometry format
            const restrictedDrawings = response.data.restrictedAreas.map(area => ({
              id: area._id || generateUniqueId(),
              type: 'polygon',
              name: area.name || 'Restricted Area',
              areaType: 'restricted',
              isHidden: area.isHidden || false,
              geometry: {
                type: 'polygon',
                latlngs: area.paths
              },
              style: {
                fillColor: area.isHidden ? 'transparent' : (area.fillColor || 'rgba(255, 0, 0, 0.35)'),
                fillOpacity: area.isHidden ? 0 : 0.35,
                color: area.isHidden ? 'transparent' : (area.strokeColor || '#FF0000'),
                weight: 3,
                opacity: area.isHidden ? 0 : 1
              },
              properties: {
                ...area.restrictionDetails,
                isRestrictedArea: true,
                _id: area._id
              }
            }));

            setDrawings(prev => [...prev.filter(d => d.areaType !== 'restricted'), ...restrictedDrawings]);
          }
        } catch (error) {
          console.error('Error fetching restricted areas:', error);
        }
      }, [props.url]);

      // Updated handlePropertyLineDrawn function
const handlePropertyLineDrawn = useCallback((layer) => {
  if (!layer || !layer.getLatLngs) return;
  
  try {
    // Get the polygon paths
    const paths = layer.getLatLngs()[0].map(latLng => ({
      lat: latLng.lat,
      lng: latLng.lng
    }));
    
    // Create a new property line object with a unique ID
    const newPropertyLineId = generateUniqueId();
    const newPropertyLine = {
      id: newPropertyLineId,
      name: `Property Line ${manualPropertyLines.length + 1}`,
      type: 'manualPropertyLine',
      paths: paths,
      isHidden: false,
      style: {
        color: '#4caf50',
        weight: 3,
        opacity: 0.8,
        fillColor: '#4caf50',
        fillOpacity: 0.1
      }
    };
    
    console.log("Adding new manual property line:", newPropertyLine);
    
    // Important: Tag the layer with the property line ID
    layer._propertyLineId = newPropertyLineId;
    
    // Style the layer properly
    layer.setStyle(newPropertyLine.style);
    
    // Add to manualPropertyLines array
    setManualPropertyLines(prev => {
      const updated = [...prev, newPropertyLine];
      console.log("Updated manualPropertyLines:", updated);
      return updated;
    });
    
    // Select the new property line
    setSelectedPropertyLineId(newPropertyLineId);
    
    showSnackbar('Property boundary created', 'success');
  } catch (error) {
    console.error("Error processing property line drawing:", error);
    showSnackbar('Error creating property boundary', 'error');
  }
}, [manualPropertyLines, generateUniqueId, showSnackbar, setSelectedPropertyLineId]);

console.log('Selected propertylineid...', selectedPropertyLineId)
    // Updated onSavePropertyLines function
    const onSavePropertyLines = useCallback(async () => {
      console.log('.....onSavePropertyLines')
      if (!jobSite?._id) {
        showSnackbar('Cannot save, no job site loaded.', 'error');
        return;
      }
      
      try {
        // showLoading('Saving property boundaries...');
        
        // Prepare data to save
        const dataToSave = {
          jobSiteId: jobSite._id,
          propertyLines: manualPropertyLines.map(line => ({
            id: line.id,
            _id: line._id,
            name: line.name,
            paths: line.paths,
            type: 'manualPropertyLine'
          }))
        };
        console.log('Send this shit...', dataToSave)
        // Send to API
        const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, dataToSave, {
          params: {
            type: 'SAVE_PROPERTY_LINES'
          }
        });
        console.log('1064...', response)
        // Update manualPropertyLines with saved versions from server (they now have _id fields)
        setManualPropertyLines(response?.data?.savedLines || []);
        
        // Clear selected property line ID
        setSelectedPropertyLineId(null);
        
        showSnackbar('Property boundaries saved successfully', 'success');
        props.notification({
          type: 'success',
          title: 'Property Lines Saved',
          message: 'Manual property boundaries have been saved'
        });
        console.log('1089')
        hideLoading();
        return null
      } catch (error) {
        hideLoading();
        console.error('Error saving property lines:', error);
        showSnackbar('Failed to save property boundaries', 'error');
        
        return null;
      } finally {
        console.log('1097...')
        hideLoading();

      }
    }, [jobSite, manualPropertyLines, props.url, props.notification, showLoading, hideLoading, showSnackbar, setPropertyLines, setManualPropertyLines, setSelectedPropertyLineId]);


      // const handlePropertyLineDrawn = useCallback((layer) => {
      //   console.log('handlePropertyLineDrawn...', layer)
      //   if (!layer || !layer.getLatLngs) return;
        
      //   try {
      //     // Get the polygon paths
      //     const paths = layer.getLatLngs()[0].map(latLng => ({
      //       lat: latLng.lat,
      //       lng: latLng.lng
      //     }));
          
      //     // Create a new property line object with a unique ID
      //     const newPropertyLineId = generateUniqueId();
      //     const newPropertyLine = {
      //       id: newPropertyLineId,
      //       name: `Property Line ${manualPropertyLines.length + 1}`,
      //       type: 'manualPropertyLine',
      //       paths: paths,
      //       isHidden: false,
      //       style: {
      //         color: '#4caf50',
      //         weight: 3,
      //         opacity: 0.8,
      //         fillColor: '#4caf50',
      //         fillOpacity: 0.1
      //       }
      //     };
          
      //     console.log("Adding new manual property line:", newPropertyLine);
          
      //     // Important: Tag the layer with the property line ID
      //     layer._propertyLineId = newPropertyLineId;
          
      //     // Style the layer properly
      //     layer.setStyle(newPropertyLine.style);
          
      //     // Add to manualPropertyLines array
      //     setManualPropertyLines(prev => {
      //       const updated = [...prev, newPropertyLine];
      //       console.log("Updated manualPropertyLines:", updated);
      //       return updated;
      //     });
          
      //     // Select the new property line
      //     setSelectedPropertyLineId(newPropertyLineId);
          
      //     showSnackbar('Property boundary created', 'success');
      //   } catch (error) {
      //     console.error("Error processing property line drawing:", error);
      //     showSnackbar('Error creating property boundary', 'error');
      //   }
      // }, [manualPropertyLines, generateUniqueId, showSnackbar, setNextDrawingIsPropertyLine, setManualPropertyLines, nextDrawingIsPropertyLineRef, setSelectedPropertyLineId]);

    
  // Add this helper function to your DynamicMap.js
  const findLeafletLayerById = useCallback((id) => {
    if (!id) return null;
    
    let foundLayer = null;
    
    // First check drawn items
    if (drawnItemsLayerRef.current) {
      drawnItemsLayerRef.current.eachLayer(layer => {
        if (layer._drawingId === id || layer._polygonId === id) {
          foundLayer = layer;
        }
      });
    }
    
    // If not found, check polygons layer
    if (!foundLayer && polygonsLayerRef.current) {
      polygonsLayerRef.current.eachLayer(layer => {
        if (layer._drawingId === id || layer._polygonId === id) {
          foundLayer = layer;
        }
      });
    }
    
    return foundLayer;
  }, [drawnItemsLayerRef, polygonsLayerRef]);

  const handleSelectPropertyLine = useCallback((id) => {
    setSelectedPropertyLineId(id);
    
    // Find the property line in the array
    const propertyLine = manualPropertyLines.find(line => line?._id === id);
    if (!propertyLine) return;
    
    // Find and highlight the layer on the map
    if (drawnItemsLayerRef.current) {
      drawnItemsLayerRef.current.eachLayer(layer => {
        if (layer._propertyLineId === id) {
          // Enable editing
          if (layer.editing) {
            layer.editing.enable();
          }
          
          // Zoom to it
          if (mapInstanceRef.current) {
            mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [50, 50] });
          }
        } else if (layer.editing && layer.editing.enabled()) {
          layer.editing.disable();
        }
      });
    }
  }, [manualPropertyLines, drawnItemsLayerRef, mapInstanceRef]);
  
  const handleTogglePropertyLineVisibility = useCallback((id) => {
    const matches = (line) => line?.id === id || line?._id === id;
    setManualPropertyLines(prev => prev.map(line =>
      matches(line) ? { ...line, isHidden: !line.isHidden } : line
    ));
    setPropertyLines(prev => prev.map(line =>
      matches(line) ? { ...line, isHidden: !line.isHidden } : line
    ));
  }, []);

  const removePropertyLine = async (id) => {
    console.log('removePropertyLineremovePropertyLine....removePropertyLine', id)
    console.log('selected', selectedPropertyLineId)
    try {
      const body = {
        type: 'DELETE_PROPERTY_LINE',
        id: id
      }
      const response = await axios.post(`${props.url}/api/hmi/genericPostRequest?type=${body.type}`, body);
      console.log('Deleted property line...', response)
    } catch (e) {
      console.log("Error deleting property Line...", e)
    }
  }
  
  const handleDeletePropertyLine = useCallback((id) => {
    console.log('...handleDeletePropertyLine..', id)
    console.log('???', selectedPropertyLineId)
    if (!window.confirm('Are you sure you want to delete this property boundary?')) return;
    removePropertyLine(id)
    // Remove from state
    setManualPropertyLines(prev => prev.filter(line => line?._id !== id));
    
    // If this was the selected one, clear selection
    if (selectedPropertyLineId === id) {
      setSelectedPropertyLineId(null);
    }
    
    // Remove from map
    if (drawnItemsLayerRef.current) {
      drawnItemsLayerRef.current.eachLayer(layer => {
        if (layer._propertyLineId === id) {
          drawnItemsLayerRef.current.removeLayer(layer);
        }
      });
    }
    
    showSnackbar('Property boundary deleted', 'info');
  }, [selectedPropertyLineId, setSelectedPropertyLineId, drawnItemsLayerRef, showSnackbar, setManualPropertyLines, removePropertyLine]);
  
  // const onSavePropertyLinesOLD = useCallback(async () => {
  //   if (!jobSite?._id) {
  //     showSnackbar('Cannot save, no job site loaded.', 'error');
  //     return;
  //   }
    
  //   try {
  //     showLoading('Saving property boundaries...');
      
  //     // Prepare data to save
  //     const dataToSave = {
  //       jobSiteId: jobSite._id,
  //       propertyLines: manualPropertyLines.map(line => ({
  //         id: line._id,
  //         _id: line._id,
  //         name: line.name,
  //         paths: line.paths,
  //         type: 'manualPropertyLine'
  //       }))
  //     };
      
  //     // Send to API
  //     // const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, dataToSave, {
  //     //   params: {
  //     //     type: 'SAVE_PROPERTY_LINES'
  //     //   }
  //     // });
  //     const convertedPropertyLines = manualPropertyLines.map(line => ({
  //       ...line,
  //       type: 'propertyLine',
  //       isHidden: false
  //     }));
  //     console.log('Set our property lines to include this', convertedPropertyLines)
  //     // Update propertyLines state by adding the converted manual property lines
  //     setPropertyLines(prevLines => [...prevLines, ...convertedPropertyLines]);
      
  //     // Remove manual property lines from drawn items layer
  //     if (drawnItemsLayerRef.current) {
  //       drawnItemsLayerRef.current.eachLayer(layer => {
  //         const propertyLineId = layer._propertyLineId;
  //         if (propertyLineId && manualPropertyLines.some(line => line?._id === propertyLineId)) {
  //           drawnItemsLayerRef.current.removeLayer(layer);
  //         }
  //       });
  //     }
      
  //     // Clear manualPropertyLines since they are now saved and converted
  //     setManualPropertyLines([]);
      
  //     // Clear selected property line ID
  //     setSelectedPropertyLineId(null);
      
  //     showSnackbar('Property boundaries saved successfully', 'success');
  //     props.notification({
  //       type: 'success',
  //       title: 'Property Lines Saved',
  //       message: 'Manual property lines have been saved'
  //     });
      
  //     // return response.data;
  //   } catch (error) {
  //     console.error('Error saving property lines:', error);
  //     showSnackbar('Failed to save property boundaries', 'error');
      
  //     return null;
  //   } finally {
  //     hideLoading();
  //   }
  // }, [jobSite, manualPropertyLines, props.url, props.notification, showLoading, hideLoading, showSnackbar, drawnItemsLayerRef]);
      

      const handleDrawCreated = useCallback((event) => {
        const layer = event.layer;
        const layerType = event.layerType;
        console.log('handleDrawCreated:', event)
        console.log('Parent: Draw CREATED - Type:', layerType, layer);
        console.log('nextDrawingIsPropertyLineRef:', nextDrawingIsPropertyLineRef.current);
        console.log('nextDrawingIsRestrictedAreaRef:', nextDrawingIsRestrictedAreaRef.current);
        if (!layer) {
          console.error("Parent: handleDrawCreated called without a layer.");
          return;
        }

        if (nextDrawingIsPropertyLineRef.current) {
            console.log("Processing as property line");
            setNextDrawingIsPropertyLine(false);
            nextDrawingIsPropertyLineRef.current = false
            handlePropertyLineDrawn(layer);
            return;
          }

        if (nextDrawingIsRestrictedAreaRef.current) {
            console.log("Processing as restricted area");
            setNextDrawingIsRestrictedArea(false);
            nextDrawingIsRestrictedAreaRef.current = false;
            handleRestrictedAreaDrawn(layer);
            return;
        }
          // Regular handling for other drawing types (polygon, etc.)
        const geometry = getGeometryFromLayer(layer);
        if (!geometry) {
          console.error("Parent: Failed to get geometry from created layer.", layer);
          return;
        }
        const paths = geometry.latlngs || [];
  
        // Calculate area and perimeter
        const geometryData = enhancedGetPolygonArea(paths);

        const newDrawing = {
          id: uuidv4(),
          _id: uuidv4(),
          name: `Untitled ${layerType}`,
          type: layerType,
          areaType: AREA_TYPES.TURF, // Default area type for new drawings
          geometry: geometry,
          style: { ...defaultStyle },
          area: geometryData.areaFeet.toFixed(2),
          areaAcres: geometryData.areaAcres.toFixed(4),
          perimeter: geometryData.perimeterFeet.toFixed(2),
          perimeterMeters: geometryData.perimeterMeters.toFixed(2)
        };
        console.log('New Drawing', newDrawing)
        setDrawings(prev => [...prev, newDrawing]);
        setSelectedDrawingId(newDrawing.id);
        console.log('Set Editing Shape  setEditingShape 1344', newDrawing)
        setEditingShape({ ...newDrawing });
      
        console.log("Parent: Added drawing data to state:", newDrawing.id);
      }, [nextDrawingIsPropertyLine, handlePropertyLineDrawn, showSnackbar, setGeofenceRadius, setGeofenceCircle, drawnItemsLayerRef, setNextDrawingIsPropertyLine, nextDrawingIsPropertyLineRef, setDrawings]);

      const testThis = (d) => {
        console.log('Testing this shit out...', d)
        console.log('pol', polygons)
        console.log(drawings)
        console.log(drawnItemsLayerRef)
        console.log(drawnItemsLayer)
      }

      const handleDrawEdited = useCallback((event) => {
        console.log('Draw EDITED event triggered', event);
        
        // Clean up orphaned edit operations
        if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer.editing && layer._editing_prev_enabled && !layer.editing.enabled()) {
              delete layer._editing_prev_enabled;
            }
          });
        }
        
        // If this is a vertex edit event
        if (event.type === 'draw:editvertex' && event.poly) {
          try {
            const polygon = event.poly;
            const polygonId = polygon._polygonId || polygon._drawingId;
            
            // CRITICAL: Check if layer is properly attached to map
            if (!polygon._map && mapInstanceRef.current) {
              console.log('Re-attaching polygon to map');
              polygon.addTo(mapInstanceRef.current);
            }
            
            // Safely extract paths with better error handling
            let newPaths = [];
            try {
              const latlngs = polygon.getLatLngs();
              // Handle potentially nested arrays
              const points = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
              
              newPaths = points.map(ll => ({
                lat: ll.lat,
                lng: ll.lng
              }));
            } catch (pathError) {
              console.error('Error extracting paths:', pathError);
              return;
            }
            const geometryData = enhancedGetPolygonArea(newPaths);
            // Create a proper geometry object
            const newGeometry = {
              type: 'polygon',
              latlngs: newPaths
            };
            
            // Find in which array this polygon exists (drawings or polygons)
            const drawingExists = drawings.some(d => d.id === polygonId);
            const polygonExists = polygons.some(p => p.id === polygonId);
            
            // Update the appropriate array
            if (polygonExists) {
              setPolygons(prev => prev.map(p => {
                if (p.id === polygonId) {
                  return { 
                    ...p, 
                    paths: newPaths,
                    geometry: newGeometry,
                    updated_at: new Date(),
                    area: geometryData.areaFeet.toFixed(2),
                    areaAcres: geometryData.areaAcres.toFixed(4),
                    perimeter: geometryData.perimeterFeet.toFixed(2),
                  };
                }
                return p;
              }));
            }
            
            if (drawingExists) {
              setDrawings(prev => prev.map(d => {
                if (d.id === polygonId) {
                  return { 
                    ...d, 
                    paths: newPaths,
                    geometry: newGeometry,
                    updated_at: new Date(),
                    area: geometryData.areaFeet.toFixed(2),
                    areaAcres: geometryData.areaAcres.toFixed(4),
                    perimeter: geometryData.perimeterFeet.toFixed(2),
                  };
                }
                return d;
              }));
            }
            
            // Update editing shape if applicable
            const editingId = currentlyEditingId || selectedDrawingId;
            
            if (editingId && editingId === polygonId) {
              setEditingShape(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  paths: newPaths,
                  geometry: newGeometry,
                  updated_at: new Date(),
                  area: geometryData.areaFeet.toFixed(2),
                  areaAcres: geometryData.areaAcres.toFixed(4),
                  perimeter: geometryData.perimeterFeet.toFixed(2),
                };
              });
            } else if (polygon._polygonData) {
              // If editing something not currently selected
              setEditingShape({
                ...polygon._polygonData,
                paths: newPaths,
                geometry: newGeometry,
                updated_at: new Date(),
                area: geometryData.areaFeet.toFixed(2),
                areaAcres: geometryData.areaAcres.toFixed(4),
                perimeter: geometryData.perimeterFeet.toFixed(2),
              });
            }
          } catch (error) {
            console.error('Error handling vertex edit:', error);
          }
          return;
        }
        
        // Handle standard layer edits
        if (event.layers) {
          event.layers.eachLayer(layer => {
            const layerId = layer._drawingId || layer._polygonId;
            if (!layerId) return;
            
            // Get geometry with better error handling
            let newGeometry = null;
            try {
              const latlngs = layer.getLatLngs();
              const points = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
              
              const paths = points.map(ll => ({
                lat: ll.lat,
                lng: ll.lng
              }));
              const geometryData = enhancedGetPolygonArea(paths);

              newGeometry = {
                type: 'polygon',
                latlngs: paths
              };
              
              // Update appropriate array
              const isDrawing = drawings.some(d => d.id === layerId);
              const isPolygon = polygons.some(p => p.id === layerId);
              
              if (isDrawing) {
                setDrawings(prev => prev.map(d => {
                  if (d.id === layerId) {
                    return { 
                      ...d, 
                      geometry: newGeometry, 
                      paths: paths,
                      area: geometryData.areaFeet.toFixed(2),
                      areaAcres: geometryData.areaAcres.toFixed(4),
                      perimeter: geometryData.perimeterFeet.toFixed(2)
                    };
                  }
                  return d;
                }));
              }
              
              if (isPolygon) {
                setPolygons(prev => prev.map(p => {
                  if (p.id === layerId) {
                    return { 
                      ...p,
                      geometry: newGeometry, 
                      paths: paths,
                      area: geometryData.areaFeet.toFixed(2),
                      areaAcres: geometryData.areaAcres.toFixed(4),
                      perimeter: geometryData.perimeterFeet.toFixed(2)
                    };
                  }
                  return p;
                }));
              }
              
              // Update editing shape if applicable
              const editingId = currentlyEditingId || selectedDrawingId;
              if (editingId && editingId === layerId) {
                setEditingShape(prev => {
                  if (!prev) return null;
                  return {
                    ...prev,
                    paths: paths,
                    geometry: newGeometry,
                    updated_at: new Date(),
                    area: geometryData.areaFeet.toFixed(2),
                    areaAcres: geometryData.areaAcres.toFixed(4),
                    perimeter: geometryData.perimeterFeet.toFixed(2),
                  };
                });
              }
            } catch (error) {
              console.error('Error handling layer edit:', error);
            }
          });
        }
      }, [
        currentlyEditingId, 
        selectedDrawingId, 
        setPolygons, 
        setDrawings, 
        setEditingShape,
        drawings,
        polygons,
        mapInstanceRef,
        drawnItemsLayerRef
      ]);

    const handleDrawDeleted = useCallback((event) => {
        console.log('Parent: Draw DELETED event');
        const deletedIds = [];
        
        event.layers.eachLayer(layer => { 
            if (layer._drawingId) deletedIds.push(layer._drawingId); 
        });

        if (deletedIds.length > 0) {
            console.log("Deleting drawings via map interaction:", deletedIds);
            setDrawings(prev => prev.filter(d => !deletedIds.includes(d.id)));
            
            if (selectedDrawingId && deletedIds.includes(selectedDrawingId)) {
                setSelectedDrawingId(null); 
                console.log('Set Editing Shape  setEditingShape 1388')
                setEditingShape(null);
            }
        }
    }, [selectedDrawingId]);

    useEffect(() => {
        console.log("Parent sees currentlyEditingId change:", currentlyEditingId);
      }, [currentlyEditingId]);
      
    useEffect(() => {
      // This effect ensures that changes to the drawings array are reflected in the drawnItems layer
      if (!drawnItemsLayerRef.current || !drawings.length) return;
      
      console.log('Synchronizing drawings with map layers', drawnItemsLayerRef);
      
      // Update each layer based on current drawing state
      drawnItemsLayerRef.current.eachLayer(layer => {
          if (!layer._drawingId) return;
          
          // Find the corresponding drawing
          const drawing = drawings.find(d => d.id === layer._drawingId);
          
          // If drawing exists and has style, update the layer style
          if (drawing && drawing.style && layer.setStyle) {
              try {
                  // Apply the merged style
                  const mergedStyle = {
                      ...defaultStyle,
                      ...drawing.style
                  };
                  
                  // Update the Leaflet layer
                  layer.setStyle(mergedStyle);
                  
                  // Ensure SVG attributes are updated directly
                  if (layer._path) {
                      layer._path.setAttribute('fill', mergedStyle.fillColor);
                      layer._path.setAttribute('fill-opacity', mergedStyle.fillOpacity);
                      layer._path.setAttribute('stroke', mergedStyle.color);
                      layer._path.setAttribute('stroke-width', mergedStyle.weight);
                      layer._path.setAttribute('stroke-opacity', mergedStyle.opacity);
                  }
                  
                  // Update the layer's popup/tooltip if applicable
                  if (layer.getTooltip && drawing.name) {
                      if (layer.getTooltip()) {
                          layer.setTooltipContent(drawing.name);
                      } else {
                          layer.bindTooltip(drawing.name);
                      }
                  }
              } catch (error) {
                  console.error('Error updating layer style:', error);
              }
          }
      });
    }, [drawings, drawnItemsLayerRef]);

    const handleSelectDrawing = useCallback((drawing) => {
      console.log('handleSelectDrawing...', drawing)
      setActiveTab(1)
      let tabChanged = false
      if (isDrawingModeActive) return;
      if (!drawing) return;
      
      // First, disable any active editing
      if (drawnItemsLayerRef.current) {
        drawnItemsLayerRef.current.eachLayer(layer => {
          if (layer.editing && layer.editing.enabled()) {
            layer.editing.disable();
          }
        });
      }
      
      if (polygonsLayerRef.current) {
        polygonsLayerRef.current.eachLayer(layer => {
          if (layer.editing && layer.editing.enabled()) {
            layer.editing.disable();
          }
        });
      }
      
      // Determine if we received an object or just an ID
      const drawingId = typeof drawing === 'object' && drawing.id ? drawing.id : drawing;
      
      // Set the currently editing ID state
      setCurrentlyEditingId(drawingId);
      setSelectedDrawingId(drawingId);
      
      // Find the full drawing object
      const drawingToSet = drawings.find(item => item.id === drawingId);
      const polygonToSet = polygons.find(item => item.id === drawingId);
      
      // Set editing shape based on what we found
      if (drawingToSet) {
        setEditingShape({ ...drawingToSet });
        
        // Find and enable editing on the layer
        if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer._drawingId === drawingId) {
              setTimeout(() => {
                if (layer.editing) {
                  layer.editing.enable();
                  // Mark this layer as being edited
                  layer._editing_prev_enabled = true;
                }
              }, 50);
            }
          });
        }
      } else if (polygonToSet) {
        setEditingShape({ ...polygonToSet });
        
        // Find and enable editing on the layer
        if (polygonsLayerRef.current) {
          polygonsLayerRef.current.eachLayer(layer => {
            if (layer._polygonId === drawingId) {
              setTimeout(() => {
                if (layer.editing) {
                  layer.editing.enable();
                  // Mark this layer as being edited
                  layer._editing_prev_enabled = true;
                }
              }, 50);
            }
          });
        }
      }


      debugState('Selecting Drawing', {
        drawingId,
        foundDrawing: drawingToSet,
        totalDrawings: drawings.length,
        isDrawingMode: isDrawingModeActive,
        totalPolygons: polygons.length,
        activeTab: activeTab,
        tabChanged: tabChanged
    });
  }, [isDrawingModeActive, drawings, drawnItemsLayerRef, mapInstanceRef, polygons, polygonsLayerRef]);

    const handleDeleteDrawing = useCallback((idToDelete) => {
      console.log('..handleDeleteDrawing', idToDelete)
        if (isDrawingModeActive) return;
        
        const drawingToDelete = drawings.find(d => d.id === idToDelete);
        if (drawingToDelete) {
          if (!window.confirm(`Delete "${drawingToDelete.name}"?`)) return;
        }
        console.log('Find polygon to delete.', polygons)
        const polygonToDelete = polygons.find(p => p.id === idToDelete)
        if (polygonToDelete) {
          if (!window.confirm(`Delete "${polygonToDelete.name}"?`)) return;
        }
        if (!drawingToDelete && !polygonToDelete) {
          console.log('Wtf...cant find it')
          return
        }

        // Remove from the map if it exists
        const layer = drawnItemsLayerRef.current?.getLayers().find(l => l._drawingId === idToDelete);
        if (layer) drawnItemsLayerRef.current.removeLayer(layer);

        // Remove from state
        if (drawingToDelete) {
          setDrawings(prev => prev.filter(d => d.id !== idToDelete));
        } 
        if (polygonToDelete) {
          setPolygons(prev => prev.filter(d => d.id !== idToDelete));
        }
        // 
        
        // Clear selection if this was the selected drawing
        if (selectedDrawingId === idToDelete) { 
            setSelectedDrawingId(null);
            console.log('Set Editing Shape  setEditingShape 1443')
            setEditingShape(null); 
        }
    }, [isDrawingModeActive, drawings, polygons, selectedDrawingId]);

    const handleClearAllDrawings = useCallback(() => {
        if (isDrawingModeActive || !window.confirm('Delete ALL saved areas? This cannot be undone.')) return;
        
        setDrawings([]);
        setPolygons([])
        polygonsLayerRef.current?.clearLayers()
        drawnItemsLayerRef.current?.clearLayers();
        setSelectedDrawingId(null); 
        console.log('Set Editing Shape  setEditingShape 1454')
        setEditingShape(null);
    }, [isDrawingModeActive]);

    const handleEditorStyleChange = useCallback((property, value) => {
      console.log(`Updating style property "${property}" to:`, value);
      console.log('Set setEditingShape')
      setEditingShape(prev => {
          if (!prev) return null;
          
          // Ensure style object exists
          const currentStyle = prev.style || { ...defaultStyle };
          
          // Create a new style object with the updated property
          const updatedStyle = {
              ...currentStyle,
              [property]: value
          };
          
          console.log('New complete style object:', updatedStyle);
          
          // Return updated shape with new style
          return {
              ...prev,
              style: updatedStyle
          };
        });
    }, [defaultStyle]);
    
    // Enhanced text field change handler
    const handleEditorChange = useCallback((event) => {
        const { name, value } = event.target;
        console.log(`Updating shape field "${name}" to:`, value);
        console.log('Set setEditingShape', name, value)
        setEditingShape(prev => {
            if (!prev) return null;
            return { ...prev, [name]: value };
        });
    }, []);

    const handleEditorSave = useCallback(() => {
      if (!editingShape || !selectedDrawingId) {
        console.warn('Cannot save: missing editingShape or selectedDrawingId');
        return;
      }
      
      console.log('Saving changes for:', selectedDrawingId);
      
      try {
        // First, determine which array this item belongs to
        const drawingExists = drawings.some(d => d.id === selectedDrawingId);
        const polygonExists = polygons.some(p => p.id === selectedDrawingId);
        
        // Get the current paths/geometry from editingShape
        const updatedGeometry = editingShape.geometry;
        const updatedPaths = editingShape.paths || 
          (updatedGeometry?.latlngs ? updatedGeometry.latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng })) : []);
        
        // Update in drawings array
        if (drawingExists) {
          setDrawings(prev => prev.map(d => {
            if (d.id === selectedDrawingId) {
              return { 
                ...d,
                name: editingShape.name,
                areaType: editingShape.areaType,
                geometry: updatedGeometry,
                paths: updatedPaths,
                style: {
                  ...(d.style || {}),
                  ...(editingShape.style || {})
                }
              };
            }
            return d;
          }));
        }
        
        // Update in polygons array
        if (polygonExists) {
          setPolygons(prev => prev.map(p => {
            if (p.id === selectedDrawingId) {
              return { 
                ...p,
                name: editingShape.name,
                areaType: editingShape.areaType,
                paths: updatedPaths,
                fillColor: editingShape.style?.fillColor || p.fillColor,
                strokeColor: editingShape.style?.color || p.strokeColor
              };
            }
            return p;
          }));
        }
        
        // Finally, update the visual layer if needed
        const layer = findLeafletLayerById(selectedDrawingId);
        if (layer && layer.setStyle) {
          try {
            const mergedStyle = {
              ...defaultStyle,
              ...(editingShape.style || {})
            };
            layer.setStyle(mergedStyle);
          } catch (e) {
            console.error("Error applying style on save:", e);
          }
        }
        
        // Clean up
        if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
            if (layer.editing && layer.editing.enabled()) {
              layer.editing.disable();
            }
          });
        }
        
        if (polygonsLayerRef.current) {
          polygonsLayerRef.current.eachLayer(layer => {
            if (layer.editing && layer.editing.enabled()) {
              layer.editing.disable();
            }
          });
        }
        
        // Clear selection
        setSelectedDrawingId(null);
        setEditingShape(null);
        
        // Show feedback
        showSnackbar('Changes saved successfully', 'success');
      } catch (error) {
        console.error('Error saving changes:', error);
        showSnackbar('Error saving changes', 'error');
      }
    }, [
      editingShape, 
      selectedDrawingId, 
      drawings, 
      polygons,
      drawnItemsLayerRef, 
      polygonsLayerRef,
      defaultStyle, 
      showSnackbar,
      setDrawings,
      setPolygons,
      findLeafletLayerById
    ]);

    const handleEditorCancel = useCallback(() => {
      console.log('handleEditorCancel...');
      
      // First, find and disable editing on any active layers
      if (drawnItemsLayerRef.current) {
          drawnItemsLayerRef.current.eachLayer(layer => {
              if (layer.editing && layer.editing.enabled()) {
                  layer.editing.disable();
              }
          });
      }
      
      if (polygonsLayerRef.current) {
          polygonsLayerRef.current.eachLayer(layer => {
              if (layer.editing && layer.editing.enabled()) {
                  layer.editing.disable();
              }
          });
      }
      
      // Then clear the state
      setSelectedDrawingId(null);
      setCurrentlyEditingId(null);
      setEditingShape(null);
  }, [drawnItemsLayerRef, polygonsLayerRef]);

    // --- Provider handling ---
    const handleHybridToggle = useCallback((isChecked) => {
        setIsHybridMode(isChecked);
        
        if (!currentProvider || currentProvider.group !== 'google') return;
        
        const targetProvider = isChecked ? googleHybridProvider : googleSatelliteProvider;
        if ((isChecked && currentProvider.name === googleSatelliteProvider.name) ||
            (!isChecked && currentProvider.name === googleHybridProvider.name)) {
            setCurrentProvider(targetProvider);
        }
    }, [currentProvider]);

    const handleProviderChange = useCallback((selectedProvider) => {
        if (!selectedProvider) return;
        
        if (selectedProvider.name === googleSatelliteProvider.name && isHybridMode) {
            setCurrentProvider(googleHybridProvider);
        } else {
            setCurrentProvider(selectedProvider);
            if (selectedProvider.group !== 'google') setIsHybridMode(false);
        }
    }, [isHybridMode]);

    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue);
        setCurrentlyEditingId(null)
        // Reset any active drawing when switching tabs
        if (drawControlInstanceRef.current) {
          // Disable any active handlers
          Object.values(drawControlInstanceRef.current._toolbars.draw._modes).forEach(mode => {
            if (mode.handler && mode.handler.enabled()) {
              mode.handler.disable();
            }
          });
        }
        
        // Cancel selection when switching tabs
        setSelectedDrawingId(null);
        console.log('Set Editing Shape  setEditingShape 1541')
        setEditingShape(null);
        
        // When switching to "Draw Areas" tab, make sure polygon tool is ready
        if (newValue === 1) {
          setDrawingMode('polygon');
        } else if (newValue === 0) {
          // When switching to "Measure property" tab, prepare for circle drawing
          setDrawingMode('circle');
        }
      }, []);


    // --- Data loading functions ---
    const fetchJobSiteData = useCallback(async (siteId) => {
        console.log("Fetching job site data for:", siteId);
        let loadedPolygons = [];
        let siteData = null;
        jobSiteAreasLoaded.current = true; // Mark as attempt started

        try {
            showLoading('Loading job site data...');
            const response = await axios.get(`${props.url}/api/customer/fetchJobSiteAreas?id=${siteId}&includeAll=1`);
            // JOBSITE_AREAS
            console.log('areas!!', response)
            const {
              success,
              jobSiteAreas
            } = response.data
            if (success) {
              console.log('We got it!!', success, jobSiteAreas)
              if (jobSiteAreas?.length) {
                console.log('We gotta add these to our site areas...')
                  loadedPolygons = jobSiteAreas.map(area => {
                    console.log('Load this area', area)
                      // First determine the path data with better validation
                      let validPaths = [];
                      
                      // Check for different possible path formats
                      if (Array.isArray(area.paths) && area.paths.length > 0) {
                          // Check if paths have the correct format
                          if ('lat' in area.paths[0] && 'lng' in area.paths[0]) {
                              validPaths = area.paths;
                          } else {
                              console.warn(`Area ${area.id || area.name} has invalid path format`, area.paths);
                          }
                      } else if (Array.isArray(area.googleMapsFormat) && area.googleMapsFormat.length > 0) {
                          // Try googleMapsFormat as fallback
                          validPaths = area.googleMapsFormat;
                      }
                      
                      // Create a properly formatted polygon object
                      return {
                          ...area,
                          id: area.id || generateUniqueId(),
                          type: 'area', // Explicitly set type to 'area' to ensure editability
                          isHidden: area.isHidden ?? false,
                          paths: validPaths, // Use the validated paths
                          
                          // Ensure style properties are properly set
                          fillColor: area.fillColor || '#ff5555',
                          strokeColor: area.strokeColor || '#ff3333'
                      };
                  });
                  console.log('Loaded polygons...', loadedPolygons)
                  // Filter out any polygons with invalid paths
                  loadedPolygons = loadedPolygons.filter(polygon => {
                      const isValid = polygon.paths && polygon.paths.length > 0;
                      if (!isValid) {
                          console.warn(`Filtered out polygon with no valid paths:`, polygon);
                      }
                      return isValid;
                  });
                  
                  console.log(`Processed ${loadedPolygons.length} areas from API.`);
                
              }
              if (response?.data?.jobSite?._id) {
                siteData = response.data.jobSite;
                console.log("Job site data received:", siteData);
                setJobSite(siteData); // Update job site info state
                // Set our geofence based on site data
                // const radius = siteData.radius || 200; // meters
                setGeofenceCircle(null)
                setGeofenceRadius(200)
                setGeofenceCenter(null)
                setTimeout(() => {
                    console.log('Redraw our geofence circle...')
                    createDefaultGeofenceCircle()
                }, 250)
              }         
               
             
                // if (siteData?.areas?.length) {
                //     loadedPolygons = siteData.areas.map(area => {
                //         // First determine the path data with better validation
                //         let validPaths = [];
                        
                //         // Check for different possible path formats
                //         if (Array.isArray(area.paths) && area.paths.length > 0) {
                //             // Check if paths have the correct format
                //             if ('lat' in area.paths[0] && 'lng' in area.paths[0]) {
                //                 validPaths = area.paths;
                //             } else {
                //                 console.warn(`Area ${area.id || area.name} has invalid path format`, area.paths);
                //             }
                //         } else if (Array.isArray(area.googleMapsFormat) && area.googleMapsFormat.length > 0) {
                //             // Try googleMapsFormat as fallback
                //             validPaths = area.googleMapsFormat;
                //         }
                        
                //         // Create a properly formatted polygon object
                //         return {
                //             ...area,
                //             id: area.id || generateUniqueId(),
                //             type: 'area', // Explicitly set type to 'area' to ensure editability
                //             isHidden: area.isHidden ?? false,
                //             paths: validPaths, // Use the validated paths
                            
                //             // Ensure style properties are properly set
                //             fillColor: area.fillColor || '#ff5555',
                //             strokeColor: area.strokeColor || '#ff3333'
                //         };
                //     });
                    
                //     // Filter out any polygons with invalid paths
                //     loadedPolygons = loadedPolygons.filter(polygon => {
                //         const isValid = polygon.paths && polygon.paths.length > 0;
                //         if (!isValid) {
                //             console.warn(`Filtered out polygon with no valid paths:`, polygon);
                //         }
                //         return isValid;
                //     });
                    
                //     console.log(`Processed ${loadedPolygons.length} areas from API.`);
                // }
                
                showSnackbar(`Loaded job site data for ${siteData.name || siteId}`, 'success');
              
            } else {
                console.error("Failed to fetch valid job site data for ID:", siteId);
                showSnackbar('Failed to load job site data', 'error');
                setJobSite(null);
                jobSiteAreasLoaded.current = false;
            }
        } catch (error) {
            console.error('Error fetching job site data:', error);
            showSnackbar('Failed to load job site data', 'error');
            setJobSite(null);
            jobSiteAreasLoaded.current = false;
        } finally {
            hideLoading()
        }
        console.log('2242 - send it back loadedPolygons', loadedPolygons)
        return { loadedPolygons, siteData };
    }, [props.url, showLoading, showSnackbar]);

    // Fetch property lines
    const fetchPropertyLines = useCallback(async (siteId) => {
        console.log("Attempting to fetch property lines for:", siteId);
        let fetchedPropertyLines = [];
        propertyLinesFetched.current = true;
        
        try {
            showLoading('Fetching property boundaries...including manual!!!.');
            props.notification({ type: 'info', title: 'Property Lines', message: 'Fetching Property Boundaries...' });

            const response = await axios.get(`${props.url}/api/customer/getAPNAndParcelInfo?id=${siteId}&includeManual=true`);
            console.log("Property lines API response received.");
            if (response?.data?.manualPropertyLines?.length) {
              console.log('got manual too!!", repsonse.d', response.data.manualPropertyLines)
              setManualPropertyLines(response.data.manualPropertyLines)
            } else {
              console.log('No manual lines..')
              setManualPropertyLines([])
            }
            if (response?.data?.propertyLines?.length) {
                fetchedPropertyLines = response.data.propertyLines
                    .filter(property => property?.googleMapsFormat?.length > 0) // Ensure paths exist
                    .map((property, index) => {
                        const parcelData = property.properties || {};
                        const paths = property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng })); // Ensure {lat, lng} format
                        return {
                            id: `prop_${siteId}_${parcelData.APN || index}`, // Create a more unique ID
                            APN: parcelData.APN,
                            address: parcelData.address,
                            paths: paths, // Use processed paths
                            type: 'propertyLine', // Standard type identifier - important for non-editability
                            isHidden: false, // Default to visible on map
                        };
                    });

                if (fetchedPropertyLines.length > 0) {
                    console.log(`Processed ${fetchedPropertyLines.length} property lines.`);
                    showSnackbar('Property boundaries loaded', 'success');
                } else {
                    console.log("No valid property boundary data found in API response (missing paths?).");
                    showSnackbar('No property boundary data available', 'info');
                }
            } else {
                console.log("No property boundary data returned from API.");
                showSnackbar('No property boundary data available', 'info');
            }
        } catch (error) {
            console.error('Error fetching property lines:', error);
            showSnackbar('Failed to fetch property boundaries', 'error');
        }
        
        console.log('Fetched Property lines:', fetchedPropertyLines);
        return fetchedPropertyLines;
    }, [props.url, props.notification, showLoading, showSnackbar]);

    const saveAreas = useCallback(async (passedDrawings) => {
      console.log('SAVE AREAS CALLED!!!');
      if (!jobSite?._id) {
        showSnackbar('Cannot save, no job site loaded.', 'error');
        return null;
      }
      
      const areasToSave = polygons.filter(p => 
        // Only include areas (not property lines)
        p.type === 'area' || p.type === 'polygon'
      );
      console.log('Areas to save...', areasToSave);

      const newDrawingsToSave = drawings.map(d => {
        // Make sure we have geometry calculations
        const paths = d.paths || (d.geometry?.latlngs?.map(ll => ({ lat: ll.lat, lng: ll.lng })) || []);
        
        // If area or perimeter is missing, calculate them now
        let area = d.area;
        let areaAcres = d.areaAcres;
        let perimeter = d.perimeter;
        
        if (!area || !perimeter) {
          const geometryData = enhancedGetPolygonArea(paths);
          area = geometryData.areaFeet.toFixed(2);
          areaAcres = geometryData.areaAcres.toFixed(4);
          perimeter = geometryData.perimeterFeet.toFixed(2);
        }
        
        // Convert drawing format to polygon format
        console.log('Convert this drawing with geometry', d);
        return {
          id: d.id,
          name: d.name,
          type: d.type === 'polygon' ? 'area' : d.type,
          areaType: d.areaType || AREA_TYPES.TURF, // Include areaType with default
          paths: paths,
          fillColor: d.style?.fillColor || defaultStyle.fillColor,
          strokeColor: d.style?.color || defaultStyle.color,
          area: area,
          areaAcres: areaAcres,
          perimeter: perimeter
        };
      });
      
      console.log('New Drawings to save with geometry data', newDrawingsToSave);
      
      try {
        showLoading('Saving areas...');
        const allAreasToSave = [
          ...areasToSave,
          ...newDrawingsToSave.filter(d => !areasToSave.some(a => a.id === d.id))
        ].map(item => {
          // Double check we have geometry data
          if (!item.area || !item.perimeter) {
            const paths = item.paths || [];
            const geometryData = enhancedGetPolygonArea(paths);
            
            return {
              ...item,
              area: geometryData.areaFeet.toFixed(2),
              areaAcres: geometryData.areaAcres.toFixed(4),
              perimeter: geometryData.perimeterFeet.toFixed(2)
            };
          }
          return item;
        });

        // Recalculate total and net areas based on the filtered list
        let totalArea = 0;
        let netArea = 0;
        let totalPerimeter = 0;
        
        allAreasToSave.forEach(item => {
          console.log('Add this item..,', item);
          // Only add to totalArea if it's not a subtraction polygon itself
          if (item.type !== 'subtraction') {
            totalArea += Number(item.area || 0); // Area already calculated
            totalPerimeter += Number(item.perimeter || 0); // Add perimeter
            
            // Calculate net area for this specific item by finding its subtractions
            const netAreaOfThisItem = calculateNetArea(item);
            netArea += Number(netAreaOfThisItem || 0);
          }
        });

        let saveData = {
          jobSite: { _id: jobSite._id, name: jobSite.name }, // Minimal job site info
          areas: allAreasToSave,
          totalArea: totalArea.toFixed(2),
          netArea: netArea.toFixed(2),
          totalPerimeter: totalPerimeter.toFixed(2) // Add total perimeter to save data
        };
        
        console.log('Saving data with geometry:', saveData);

        const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, saveData, {
          params: {
            type: 'SAVE_HERBICIDE_AREAS'
          }
        });

        showSnackbar('Areas saved successfully', 'success');
        props.notification({
          type: 'success',
          title: 'Areas Saved',
          message: 'All areas have been saved successfully'
        });
        
        return response.data;
      } catch (error) {
        console.error('Error saving areas:', error.response?.data || error.message);
        showSnackbar(`Failed to save areas: ${error.response?.data?.message || 'Server error'}`, 'error');
        props.notification({
          type: 'error',
          title: 'Error',
          message: 'Failed to save areas'
        });
        
        return null; // Indicate failure
      } finally {
        hideLoading();
      }
    }, [polygons, jobSite, calculateNetArea, drawings, props.url, props.notification, showLoading, hideLoading, showSnackbar, enhancedGetPolygonArea]);
    // const saveAreasOLD = useCallback(async (passedDrawings) => {
    //     console.log('SAVE AREAS CALLED!!!');
    //     if (!jobSite?._id) {
    //         showSnackbar('Cannot save, no job site loaded.', 'error');
    //         return null;
    //     }
        
    //     try {
    //         showLoading('Saving areas...');
            
    //         // Determine which drawings to use
    //         let drawingsToUse = drawings;
    //         if (passedDrawings) {
    //             console.log('Using passed drawings:', passedDrawings);
    //             drawingsToUse = passedDrawings;
    //         }
            
    //         // Filter out property lines and ensure areas have calculated area property
    //         const areaPolygonsToSave = drawingsToUse
    //             .filter(p => p.type !== 'propertyLine') // Exclude property lines
    //             .map(item => {
    //                 console.log('Processing shape for save:', item);
                    
    //                 // Ensure we have paths from geometry if needed
    //                 if (item.geometry?.latlngs && (!item.paths || item.paths.length === 0)) {
    //                     item.paths = item.geometry.latlngs.flat().map(({ lat, lng }) => ({ lat, lng }));
    //                     item.polygonType = 'area';
    //                 }
                    
    //                 // Extract style properties if needed
    //                 if (item.style) {
    //                     item.fillColor = item.style.fillColor;
    //                     item.strokeColor = item.style.color;
    //                 }
                    
    //                 // Calculate area
    //                 const areaInfo = getPolygonArea(item.paths);
                    
    //                 // Create clean item for API
    //                 return {
    //                     ...item,
    //                     area: (areaInfo?.areaFeet ?? 0).toFixed(2), // Add/update calculated area
    //                     // Ensure required fields for API
    //                     id: item.id,
    //                     name: item.name || `Area ${item.id.slice(0, 4)}`,
    //                     paths: item.paths,
    //                     type: item.type,
    //                     polygonType: item.polygonType || 'area',
    //                     fillColor: item.fillColor || '#ff5555',
    //                     strokeColor: item.strokeColor || '#ff3333',
    //                 };
    //             });
    //             // Recalculate total and net areas based on the filtered list
    //         let totalArea = 0;
    //         let netArea = 0;
    //         areaPolygonsToSave.forEach(item => {
    //             // Only add to totalArea if it's not a subtraction polygon itself
    //             if (item.type !== 'subtraction') {
    //                 totalArea += Number(item.area); // Area already calculated
    //                 // Calculate net area for this specific item by finding its subtractions
    //                 const netAreaOfThisItem = calculateNetArea(item);
    //                 netArea += Number(netAreaOfThisItem);
    //             }
    //         });

    //         let saveData = {
    //             jobSite: { _id: jobSite._id, name: jobSite.name }, // Minimal job site info
    //             areas: areaPolygonsToSave,
    //             totalArea: totalArea.toFixed(2),
    //             netArea: netArea.toFixed(2)
    //         };
            
    //         console.log('Saving data:', saveData);

    //         const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, saveData, {
    //             params: {
    //                 type: 'SAVE_HERBICIDE_AREAS'
    //             }
    //         });

    //         showSnackbar('Areas saved successfully', 'success');
    //         props.notification({ 
    //             type: 'success', 
    //             title: 'Areas Saved', 
    //             message: 'All areas have been saved successfully' 
    //         });
            
    //         return response.data;
    //     } catch (error) {
    //         console.error('Error saving areas:', error.response?.data || error.message);
    //         showSnackbar(`Failed to save areas: ${error.response?.data?.message || 'Server error'}`, 'error');
    //         props.notification({ 
    //             type: 'error', 
    //             title: 'Error', 
    //             message: 'Failed to save areas' 
    //         });
            
    //         return null; // Indicate failure
    //     } finally {
    //         hideLoading();
    //     }
    // }, [polygons, jobSite, calculateNetArea, drawings, props.url, props.notification, showLoading, hideLoading, showSnackbar, getPolygonArea]);

    const handleChangeSite = useCallback((newSite) => {
      console.log('Change site!', newSite)
        const newSiteId = newSite?._id;
        const currentSiteId = jobsiteId; // Get current ID from params

        if (newSiteId && newSiteId !== currentSiteId) {
            console.log(`Site change requested via UI to: ${newSiteId}`);
            // Reset loading flags to allow the main useEffect to trigger a full reload
            jobSiteAreasLoaded.current = false;
            propertyLinesFetched.current = false;
            // Navigate to the new URL, which will change `jobsiteId` param and trigger the main load effect
            history.replace(`/clientmapview/${newSiteId}`);
        } else {
            console.log("Site change requested, but ID is missing or same as current.");
        }
    }, [jobsiteId, history]);

    const handleSaveAreas = async () => {
        console.log('Save our areas...', drawings);
        saveAreas(drawings);
    };
    const handleOpenGoogleStreetView = useCallback(() => {
      if (!jobSite) {
        showSnackbar("No job site selected.", "warning");
        return;
      }
    
      const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
      const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
    
      if (!lat || !lng) {
        console.warn("Missing coordinates for Street View.");
        showSnackbar("Could not determine location for Street View.", "warning");
        return;
      }
    
      const streetViewURL = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
    
      const streetViewTab = window.open(streetViewURL, "_blank");
      if (streetViewTab) {
        streetViewTab.focus();
      } else {
        showSnackbar("Popup blocked. Please allow popups to view Street View.", "warning");
      }
    }, [jobSite, center, showSnackbar]);

    const handleOpenMaricopaMaps = () => {
      console.log('handleOpenMaricopaMaps...')
      const addy = `${jobSite.address} ${jobSite.city}, AZ ${jobSite.zip}`;
      const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
      const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
    
      if (lat && lng) {
        const altMapURL = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${addy}`;
        console.log('2605...', altMapURL)
        // Open another tab for this alternate address if needed:
        window.open(altMapURL, "_blank")?.focus();
      }
    }
    
    const handleSearchMCAssessor = async () => {
      console.log('handleSearchMCAssessor...', jobSite);
      let addy = `${jobSite.address}, ${jobSite.city}, ${jobSite.zip}`

      // https://mcassessor.maricopa.gov/mcs/?q=13995%20w%20statler
     
      // console.log('hit this, too', apnURL)
      // window.open(apnURL, "_blank")?.focus();

      console.log('Now lets find the map, too...')
      let searchURL = `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/AssessorCompositeLocator/GeocodeServer/findAddressCandidates?SingleLine=${encodeURIComponent(addy)}&f=json`
      console.log('Search URL', searchURL)
      const response = await axios.get(searchURL)
      console.log('search results...', response)
      if (response?.data?.candidates?.length) {
        console.log('use the first one??', response.data.candidates[0])
        const addressCandidate = response.data.candidates[0]
        console.log('encode this fucker...', addressCandidate)
        let encodedAddy = encodeURIComponent(addressCandidate.address)
        console.log('Encoded Addy', encodedAddy)
        doesThisMatch(addressCandidate.address, addy)
        let apnURL = `https://mcassessor.maricopa.gov/mcs/?q=${encodedAddy}`
        window.open(apnURL, "_blank")?.focus();
      } else {
        showSnackbar("Could not find a matching address. Are we sure this is legit?", "warning");
      }
    }

    const normalizeAddress = (str) => {
      return str
        ?.toLowerCase()
        .replace(/[^\w\s]|_/g, '') // remove punctuation
        .replace(/\s+/g, '');      // remove all spaces
    };

    const doesThisMatch = (mcAddy, addy) => {
      console.log('do they match', mcAddy, addy)
      let formattedMC = normalizeAddress(mcAddy)
      let formattedAddy = normalizeAddress(addy)
      console.log(formattedMC)
      console.log(formattedAddy)
      let theyMatch = formattedMC === formattedAddy
      if (!theyMatch) {
        console.log('Better update our jobsite fucker...', mcAddy)
        setShowAPNInput(true)
      }
    }
    // const otherShit = () => {
    //   const apnURL = `https://mcassessor.maricopa.gov/mcs/?q=${encodeURIComponent(jobSite.address)}`;
    //   const searchURL = `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/AssessorCompositeLocator/GeocodeServer/findAddressCandidates?SingleLine=${encodedAddy}&f=json`;
    
    //   // 👉 Open both windows immediately to avoid popup blockers
    //   const assessorTab = window.open('', '_blank');
    //   // const mapTab = window.open('', '_blank');
    
    //   // Start loading messages
    //   assessorTab?.document.write('<p>Loading MC Assessor Info...</p>');
    //   mapTab?.document.write('<p>Loading Map View...</p>');
    
    //   // Redirect Assessor tab right away
    //   if (assessorTab) {
    //     assessorTab.location.href = apnURL;
    //   }
    
    //   try {
    //     const response = await axios.get(searchURL);
    //     const candidate = response?.data?.candidates?.[0];
    //     if (candidate) {
    //       const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
    //       const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
        
    //       if (lat && lng && mapTab) {
    //         const mapURL = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${candidate.address}`;
    //         mapTab.location.href = mapURL;
    //       } else {
    //         mapTab?.close(); // 💥 Avoid calling close() on null
    //         console.warn("Missing lat/lng or mapTab was blocked.");
    //         showSnackbar("Could not determine map coordinates.", "warning");
    //       }
    //     } else {
    //       mapTab?.close(); // 💥 Use optional chaining
    //       console.warn("No address candidates found.");
    //       showSnackbar("No location data found for this address.", "warning");
    //     }
        
    //     // if (candidate) {
    //     //   const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
    //     //   const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
    
    //     //   if (lat && lng) {
    //     //     const mapURL = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${candidate.address}`;
    //     //     mapTab.location.href = mapURL;
    //     //   } else {
    //     //     mapTab.close();
    //     //     console.warn('Missing lat/lng, closing map tab.');
    //     //     showSnackbar("Could not determine map coordinates.", "warning");
    //     //   }
    //     // } else {
    //     //   mapTab.close();
    //     //   console.warn('No address candidates found.');
    //     //   showSnackbar("No location data found for this address.", "warning");
    //     // }
    //   } catch (err) {
    //     console.error('Geocode fetch failed:', err);
    //     mapTab.close();
    //     showSnackbar("Failed to fetch mapping info.", "error");
    //   }
    
    //   // 🔍 Bonus: Property line check (separate from map tab above)
    //   const propertyLineWithAddress = polygons.find(p => p.type === 'propertyLine' && p.address);
    //   if (propertyLineWithAddress) {
    //     const cleanedAddy = encodeURIComponent(
    //       propertyLineWithAddress.address.replace(/,/g, "").replace(/\s+/g, " ").trim()
    //     );
    
    //     const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
    //     const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
    
    //     if (lat && lng) {
    //       const altMapURL = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${cleanedAddy}`;
    //       // Open another tab for this alternate address if needed:
    //       const altMapTab = window.open(altMapURL, "_blank");
    //       altMapTab?.focus();
    //     } else {
    //       console.warn("Could not determine Lat/Lng for alternate address search.");
    //       showSnackbar("Could not determine location for property line search.", "warning");
    //     }
    //   } else {
    //     console.warn("No property line with address found.");
    //     showSnackbar("No property line with address found.", "warning");
    //   }
    // }, [polygons, jobSite, center, showSnackbar]);
    
    
    // const handleSearchMCAssessorOLD = useCallback(async () => {
    //   console.log('handleSearchMCAssessor...', jobSite)
    //     let addy = `${jobSite.address} ${jobSite.city}, AZ ${jobSite.zip}`
    //     let encodedAddy = encodeURIComponent(addy)
    //     console.log('Encoded Addy', encodedAddy)
    //     // https://mcassessor.maricopa.gov/mcs/?q=13995%20w%20statler
    //     let apnURL = `https://mcassessor.maricopa.gov/mcs/?q=${encodeURIComponent(jobSite.address)}`
    //     console.log('hit this, too', apnURL)
    
    //     console.log('Now lets find the map, too...')
    //     let searchURL = `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/AssessorCompositeLocator/GeocodeServer/findAddressCandidates?SingleLine=${encodedAddy}&f=json`
    //     console.log('Search URL', searchURL)
    //     const response = await axios.get(searchURL)
    //     console.log('search results...', response)
    //     if (response?.data?.candidates?.length) {
    //       console.log('use the first one??', response.data.candidates[0])
    //       const addressCandidate = response.data.candidates[0]
    //       const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
    //       const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];
    //       console.log('got lat and logn too', lat, lng)
    //       if (lat && lng) {
    //         let url = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${addressCandidate.address}`;
    //         console.log("Opening MC Assessor URL:", url);
    //         window.open(url, "_blank")?.focus();
          
    //       }
    //       window.open(apnURL, "_blank")?.focus();
    //     }
    //     // Find the first property line polygon with an address
    //     const propertyLineWithAddress = polygons.find(p => p.type === 'propertyLine' && p.address);

    //     if (propertyLineWithAddress) {
    //         let addy = propertyLineWithAddress.address.replace(/,/g, "").replace(/\s+/g, " ").trim();
    //         addy = encodeURIComponent(addy).replace(/%2C/g, ""); // Ensure encoding removes commas

    //         // Get Lat/Lng - prioritize jobSite state, fallback if needed
    //         const lat = jobSite?.location?.coordinates?.[1] || jobSite?.latitude || center[0];
    //         const lng = jobSite?.location?.coordinates?.[0] || jobSite?.longitude || center[1];

    //         if (lat && lng) {
    //             let url = `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${lat}&2=${lng}&a=${addy}`;
    //             console.log("Opening MC Assessor URL:", url);
    //             window.open(url, "_blank")?.focus();
    //         } else {
    //             console.warn("Could not determine Lat/Lng for MC Assessor search.");
    //             showSnackbar("Could not determine location for search.", "warning");
    //         }
    //     } else {
    //         console.warn("No property line with an address found to perform MC Assessor search.");
    //         showSnackbar("No property line with address found.", "warning");
    //     }
    // }, [polygons, jobSite, center, showSnackbar]);

    // --- useEffect hooks ---
    
    // Load Maricopa providers on mount
    useEffect(() => {
        const fetchProviders = async () => {
            setYearProvidersLoading(true);
            setInitError(null);
            await loadEsriLeaflet();

            const currentActualYear = new Date().getFullYear();
            const yearsToAttempt = [currentActualYear, currentActualYear - 1, currentActualYear - 2];
            console.log("Checking availability for years:", yearsToAttempt);

            const promises = yearsToAttempt.map(year => checkYearService(year));
            const results = await Promise.all(promises);
            const availableYears = results.filter(Boolean).sort((a, b) => b.year - a.year);
            console.log('Availability', results, availableYears)
            console.log("Available Maricopa providers:", availableYears.map(p => p.year));
            const allProviders = [googleHybridProvider, googleSatelliteProvider, googleMapsProvider, ...availableYears];
            setProviders(allProviders);

            // Default to Google Hybrid (satellite with labels) — most useful for site work
            setCurrentProvider(googleHybridProvider);

            setYearProvidersLoading(false);
        };
        
        fetchProviders().catch(err => {
            console.error("Critical error fetching providers:", err);
            setInitError("Failed to load map services information. Please try refreshing.");
            setYearProvidersLoading(false);
        });
    }, []);

    // Restore Map Bounds from session storage
    useEffect(() => {
        const storedBoundsData = sessionStorage.getItem('maricopa-map-view');
        if (storedBoundsData) {
            try {
                const data = JSON.parse(storedBoundsData);
                if (data.center?.lat && data.center?.lng && data.zoom) {
                    setCurrentCenter([data.center.lat, data.center.lng]);
                    setCurrentZoom(data.zoom);
                    if (data.bounds) mapBoundsRef.current = data.bounds;
                    console.log('Restored map view from session storage.');
                }
            } catch (e) { 
                console.error('Error parsing stored map view:', e); 
            }
        }
    }, []);

    // Load site data when jobsiteId changes
    useEffect(() => {
        let isMounted = true;
        console.log("Initial data load effect running. JobsiteId:", jobsiteId);

        // Function to load all data for the current jobsiteId
        const loadInitialData = async () => {
          console.log('2539......loadInitialData...')
            if (!jobsiteId) {
                console.log("No jobsiteId, resetting state.");
                setJobSite(null);
                setPolygons([]);
                setCenter(defaultCenterCoords);
                setZoom(defaultZoom);
                setSelectedPolygonIndex(null);
                jobSiteAreasLoaded.current = false;
                propertyLinesFetched.current = false;
                hideLoading();
                return;
            }

            // Only proceed if data hasn't been loaded/attempted for this ID yet
            if (jobSiteAreasLoaded.current && propertyLinesFetched.current && jobSite?._id === jobsiteId) {
                console.log("Data already loaded for this jobsiteId, skipping fetch.");
                hideLoading();
                return;
            }

            console.log(`Starting data load sequence for jobsiteId: ${jobsiteId}`);
            showLoading('Loading map data...'); // General loading message

            // Reset flags for the current attempt
            jobSiteAreasLoaded.current = false;
            propertyLinesFetched.current = false;
            setPolygons([]); // Clear previous polygons immediately
            setSelectedPolygonIndex(null);
            const ourSiteData = await fetchJobSiteData(jobsiteId);
            console.log('Got our site Data 2663!!!', ourSiteData)
            // 1. Fetch Job Site Data (includes areas)
            const { loadedPolygons: initialAreas, siteData } = ourSiteData
            console.log('2665...', initialAreas, siteData)
            if (!isMounted || !siteData) {
                console.log("Exiting initial load: unmounted or siteData fetch failed.");
                jobSiteAreasLoaded.current = false; // Reset flag if fetch failed
                hideLoading();
                return;
            }
            console.log('Got some loaded polygons!!!! 2672')
            // 2. Fetch Property Lines (only if site data loaded successfully)
            const fetchedPropertyLines = await fetchPropertyLines(jobsiteId);
            console.log('Property lines to display:', fetchedPropertyLines);

            // 3. Fetch Restricted Areas
            await fetchRestrictedAreas(jobsiteId);
           
            if (!isMounted) {
                console.log("Exiting initial load: unmounted after property line fetch.");
                hideLoading();
                return;
            }
            setPropertyLines(fetchedPropertyLines)
            hideLoading();
            // 3. Combine results and Update Polygons State ONCE
            console.log("Combining initial areas and fetched property lines.");
            console.log('Set Initial areas as drawings...', initialAreas);
            // setDrawings(initialAreas);
            const combinedPolygons = [...initialAreas] // , ...fetchedPropertyLines];
            console.log('Set these new polygons...', combinedPolygons);
            // Add our geofence circle
            console.log('Do we have our circle??', geofenceCenter, geofenceCircle, geofenceRadius)
            setPolygons(combinedPolygons);
            // 4. Determine Map View and Update Center/Zoom State ONCE
            let newCenterCoords = defaultCenterCoords;
            let newZoomLevel = defaultZoom;

            // Prioritize job site location from fetched data
            // Guard against [0,0] / 0,0 which are placeholder values, not real coordinates
            const coords = siteData.location?.coordinates;
            const hasValidCoords = coords?.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
            const hasValidLatLng = siteData.latitude && siteData.longitude;

            if (hasValidCoords) {
                // API format is [lng, lat], Leaflet needs [lat, lng]
                newCenterCoords = [coords[1], coords[0]];
            } else if (hasValidLatLng) {
                newCenterCoords = [siteData.latitude, siteData.longitude];
            } else {
                console.warn("Job site location data missing or invalid, using default center.");
                setInvalidCoordsDialogOpen(true);
            }

            // Adjust zoom based on loaded content
            if (fetchedPropertyLines.length > 0) {
                newZoomLevel = 18; // Zoom closer if property lines are loaded
            } else if (initialAreas.length > 0) {
                newZoomLevel = 17; // Zoom less close if only areas loaded
            } else {
                newZoomLevel = 16; // Default zoom if nothing specific loaded but site exists
            }

            console.log(`Setting initial map view state - Center: ${newCenterCoords}, Zoom: ${newZoomLevel}`);
            setCenter(newCenterCoords);
            setZoom(newZoomLevel);

            hideLoading(); // Hide loading indicator
            console.log("Initial data load sequence complete.");
        };

        loadInitialData();

        // Cleanup function
        return () => {
            console.log("Initial data load effect cleanup.");
            isMounted = false;
        };
    }, [jobsiteId, fetchJobSiteData, fetchPropertyLines, fetchRestrictedAreas, showLoading, hideLoading]);

    // Apply vertex styling when map instance is ready
    useEffect(() => {
        if (mapInstanceRef.current) {
            ensureVertexStyling();
        }
    }, [mapInstanceRef.current, ensureVertexStyling]);

    // Add this useEffect in DynamicMap.js after the other useEffect hooks
    useEffect(() => {
        // When Tab 0 is active, ensure geofence circle is created and displayed
        if (activeTab === 0 && jobSite && mapInstanceRef.current) {
        console.log('Tab 0 active - ensuring geofence circle is displayed');
        
        // If we don't have a circle yet, create one
        if (!geofenceCircle) {
            createDefaultGeofenceCircle();
        } else {
            // If circle exists but isn't on map, add it
            if (!geofenceCircle._map) {
            attachGeofenceToMap();
            }
        }
        }
    }, [activeTab, jobSite, mapInstanceRef.current, geofenceCircle, createDefaultGeofenceCircle, attachGeofenceToMap]);

    const handleDrawingGeometryChanged = useCallback((drawingId, newGeometry) => {
      console.log(`Geometry updated for drawing: ${drawingId}`, newGeometry);
      
      // Update the geometry in the editingShape if it's the one being edited
      if (selectedDrawingId === drawingId && editingShape) {
        console.log('set setEditingShape', newGeometry)
        setEditingShape(prev => ({
          ...prev,
          geometry: newGeometry
        }));
      }
  
      setDrawings(prev => prev.map(d => 
        d.id === drawingId 
          ? { ...d, geometry: newGeometry } 
          : d
      ));
      
    }, [selectedDrawingId, editingShape]);


    // console.log('Currently Editing Id...', currentlyEditingId)
    // ------- RENDER SECTION -------
    return (
        <div className={classes.container}>
            <Typography variant="h4" className={classes.title}>Jobsite Aerial Imagery</Typography>
            {/* <div>Next Drawing is Property Line: {nextDrawingIsPropertyLine.toString()} ref: {nextDrawingIsPropertyLineRef?.current?.toString()}</div> */}
            <Paper elevation={3} style={{ padding: "20px" }}>
                {/* Main Content Area */}
                <Box className={classes.contentWrapper}>
                    {/* Map Section */}
                    <Box className={classes.mapWrapper}>
                        <MapComponent
                            provider={currentProvider}
                            drawings={drawings}
                            polygons={polygons}
                            center={center}
                            zoom={zoom}
                            initialCenter={defaultCenterCoords}
                            initialZoom={defaultZoom}
                            onMapReady={handleMapReady}
                            onMapViewChange={handleMapViewChange}
                            onDrawCreated={handleDrawCreated}
                            onDrawEdited={handleDrawEdited}
                            onDrawDeleted={handleDrawDeleted}
                            onDrawStart={handleDrawStart}
                            onDrawStop={handleDrawStop}
                            drawingMode={drawingMode}
                            isDrawingToolActive={isDrawingModeActive}
                            providers={providers}
                            onProviderChange={handleProviderChange}
                            loading={loading}
                            loadingMessage={loadingMessage}
                            onPolygonEdited={handlePolygonEdited}
                            currentlyEditingId={currentlyEditingId}
                            selectDrawing={handleSelectDrawing}
                            currentlyEditingIdRef={currentlyEditingIdRef}
                            propertyLines={[...propertyLines, ...manualPropertyLines]}
                            onGeofenceRadiusChange={handleGeofenceRadiusChange}
                            onGeofenceMoved={handleGeofenceMoved}
                            geofenceCircle={geofenceCircle}
                            onGeofenceEditingChanged={setIsEditingGeofence}
                            activeTab={activeTab}
                            manualPropertyLines={manualPropertyLines}
                            onDrawingGeometryChanged={handleDrawingGeometryChanged}
                            restrictedAreas={restrictedAreas}
                        />
                        
                        <Box mt={1} display="flex" justifyContent="space-between" flexWrap="wrap">
                            <Typography variant="caption" color="textSecondary">
                                Zoom: {zoom?.toFixed(1)}
                            </Typography>
                            {currentProvider && (
                                <Typography 
                                    variant="caption" 
                                    color="textSecondary" 
                                    dangerouslySetInnerHTML={{ __html: currentProvider.attribution }} 
                                />
                            )}
                        </Box>
                    </Box>
                    
                    {/* Sidebar */}
                    <Box className={classes.sidebarContainer}>
                        <Sidebar
                            polygons={polygons}
                            selectedPolygonIndex={selectedPolygonIndex}
                            jobSite={jobSite}
                            activeTab={activeTab}
                            onToggleVisibility={toggleVisibility}
                            onSaveAreas={saveAreas}
                            onTabChange={handleTabChange}
                            onChangeSite={handleChangeSite}
                            onSearchMCAssessor={handleSearchMCAssessor}
                            onOpenGoogleStreetView={handleOpenGoogleStreetView}
                            onOpenMCMaps={handleOpenMaricopaMaps}
                            calculateNetArea={calculateNetArea}
                            onUpdatePolygon={updatePolygon}
                            onDeletePolygon={deletePolygon}
                            onSelectPolygon={handleSelectDrawing}
                            clients={props.clients}
                            url={props.url}
                            onProviderChange={handleProviderChange}
                            currentProvider={selectedProvider}
                            availableProviders={providers}
                            onStartGeofenceDrawing={handleStartGeofenceDrawing}
                            onSaveGeofenceArea={handleSaveGeofenceArea}
                            onUpdateGeofenceRadius={handleUpdateGeofenceRadius}
                            isEditingGeofence={isEditingGeofence}
                            hasGeofenceCircle={!!geofenceCircle}
                            propertyLines={propertyLines}
                            manualPropertyLines={manualPropertyLines}
                            selectedPropertyLineId={selectedPropertyLineId}
                            onStartPropertyLineDrawing={handleStartPropertyLineDrawing}
                            onSelectPropertyLine={handleSelectPropertyLine}
                            onTogglePropertyLineVisibility={handleTogglePropertyLineVisibility}
                            onDeletePropertyLine={handleDeletePropertyLine}
                            onSavePropertyLines={onSavePropertyLines}
                            restrictedAreas={restrictedAreas}
                            onAddRestrictedArea={handleStartRestrictedAreaDrawing}
                            onEditRestrictedArea={handleEditRestrictedArea}
                            onDeleteRestrictedArea={handleDeleteRestrictedArea}
                            onToggleRestrictedAreaVisibility={handleToggleRestrictedAreaVisibility}
                            selectedRestrictedAreaId={selectedRestrictedAreaId}
                            onSelectRestrictedArea={handleSelectRestrictedArea}
                        />
                    </Box>
                </Box>
                {
                    activeTab === 1 && (
                        <Box className={classes.controlPanel}>
                        <Typography variant="h6" gutterBottom>Drawing & Areas</Typography>
                        
                        <Grid container spacing={2}>
                            {/* Drawing List */}
                            <Grid item xs={12} sm={8} md={4}>
                                <DrawingList
                                  drawings={drawings}
                                  polygons={polygons}
                                  selectedDrawingId={selectedDrawingId || currentlyEditingId}
                                  onSelectDrawing={handleSelectDrawing}
                                  onDeleteDrawing={handleDeleteDrawing}
                                  onClearAll={handleClearAllDrawings}
                                  isDrawingMode={isDrawingModeActive}
                                  listStyle={classes.drawingList}
                                  onSaveAll={handleSaveAreas}
                                />
                            </Grid>
                            
                            {/* Drawing Editor */}
                            <Grid item xs={12} md={5}>
                                <DrawingEditor
                                    editingShape={editingShape}
                                    onEditChange={handleEditorChange}
                                    onStyleChange={handleEditorStyleChange}
                                    onSave={handleEditorSave}
                                    onCancel={handleEditorCancel}
                                    isDrawingMode={isDrawingModeActive}
                                    colorSwatchStyle={classes.colorSwatch}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    )
                }

            </Paper>
            
            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={hideSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={hideSnackbar} severity={snackbar.type}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Restricted Area Dialog */}
            <RestrictedAreaDialog
                open={restrictedAreaDialogOpen}
                onClose={() => {
                    console.log("Closing restricted area dialog - checking if cancelled or saved");

                    // Check if any layers were actually saved before removing
                    let hasSavedLayers = false;
                    if (drawnItemsLayerRef.current) {
                        drawnItemsLayerRef.current.eachLayer((layer) => {
                            if (layer._isTemporaryRestrictedArea && layer._saved) {
                                hasSavedLayers = true;
                            }
                        });
                    }

                    console.log("Has saved layers:", hasSavedLayers);

                    // Only remove unsaved temporary layers
                    if (drawnItemsLayerRef.current && !hasSavedLayers) {
                        drawnItemsLayerRef.current.eachLayer((layer) => {
                            // Only remove if it's a temporary restricted area that wasn't saved
                            if (layer._isTemporaryRestrictedArea && !layer._saved) {
                                console.log("Removing unsaved temporary restricted area layer");
                                drawnItemsLayerRef.current.removeLayer(layer);
                            }
                        });
                    }

                    setRestrictedAreaDialogOpen(false);
                    setTempRestrictedAreaPaths([]);
                    setEditingRestrictedArea(null);
                }}
                onSave={handleSaveRestrictedArea}
                editingArea={editingRestrictedArea}
                paths={tempRestrictedAreaPaths}
                clientId={jobSite?.client?._id || jobSite?.client}
                url={props.url}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialog.open}
                onClose={() => setDeleteConfirmDialog({ open: false, areaId: null, areaName: '' })}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning style={{ color: '#ff0000' }} />
                        Delete Restricted Area
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the restricted area <strong>"{deleteConfirmDialog.areaName}"</strong>?
                        <br /><br />
                        This action cannot be undone and will remove all access restrictions and details associated with this area.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteConfirmDialog({ open: false, areaId: null, areaName: '' })}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDeleteRestrictedArea}
                        color="secondary"
                        variant="contained"
                        style={{ backgroundColor: '#ff0000', color: 'white' }}
                        autoFocus
                    >
                        Delete Area
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Invalid Coordinates Warning Dialog */}
            <Dialog
                open={invalidCoordsDialogOpen}
                onClose={() => { if (!savingCoords) setInvalidCoordsDialogOpen(false); }}
                aria-labelledby="invalid-coords-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="invalid-coords-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning style={{ color: '#ff9800' }} />
                        Missing Location Data
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {jobSite ? (
                            <>
                                The job site <strong>"{jobSite.name || jobSite.jobNumber || 'Unknown'}"</strong>
                                {jobSite.address ? ` at ${jobSite.address}` : ''} does not have valid coordinates.
                            </>
                        ) : (
                            <>This job site does not have valid coordinates.</>
                        )}
                        <br /><br />
                        The map is showing the default location. You can manually enter GPS coordinates below to fix this.
                        Tip: right-click any location in Google Maps and copy the coordinates.
                    </DialogContentText>
                    <Box display="flex" gap={2} mt={2}>
                        <TextField
                            label="Latitude"
                            placeholder="e.g. 33.4484"
                            value={manualLat}
                            onChange={(e) => setManualLat(e.target.value)}
                            type="number"
                            inputProps={{ step: 'any' }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={savingCoords}
                        />
                        <TextField
                            label="Longitude"
                            placeholder="e.g. -112.0742"
                            value={manualLng}
                            onChange={(e) => setManualLng(e.target.value)}
                            type="number"
                            inputProps={{ step: 'any' }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={savingCoords}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setInvalidCoordsDialogOpen(false);
                            setManualLat('');
                            setManualLng('');
                        }}
                        color="default"
                        disabled={savingCoords}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={async () => {
                            const lat = parseFloat(manualLat);
                            const lng = parseFloat(manualLng);
                            if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                                showSnackbar('Please enter valid coordinates (Lat: -90 to 90, Lng: -180 to 180)', 'error');
                                return;
                            }
                            if (!jobSite?._id) {
                                showSnackbar('No job site loaded to update', 'error');
                                return;
                            }
                            setSavingCoords(true);
                            try {
                                await axios.post(`${props.url}/api/customer/updateJobSite?coordsOnly=1`, {
                                    data: {
                                        _id: jobSite._id,
                                        latitude: lat,
                                        longitude: lng,
                                    }
                                });
                                // Update local state so map re-centers
                                const updatedSite = {
                                    ...jobSite,
                                    latitude: lat,
                                    longitude: lng,
                                    location: { type: 'Point', coordinates: [lng, lat] }
                                };
                                setJobSite(updatedSite);
                                setCenter([lat, lng]);
                                setZoom(17);
                                setInvalidCoordsDialogOpen(false);
                                setManualLat('');
                                setManualLng('');
                                showSnackbar('Coordinates saved successfully!', 'success');
                            } catch (err) {
                                console.error('Error saving coordinates:', err);
                                showSnackbar('Failed to save coordinates. Please try again.', 'error');
                            } finally {
                                setSavingCoords(false);
                            }
                        }}
                        color="primary"
                        variant="contained"
                        disabled={savingCoords || !manualLat || !manualLng}
                    >
                        {savingCoords ? <CircularProgress size={20} style={{ marginRight: 8 }} /> : null}
                        {savingCoords ? 'Saving...' : 'Save & Center Map'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        url: state.url,
        user: state.user,
        notification: state.notification,
        clients: state.clients,
    };
};

export default connect(mapStateToProps)(React.memo(DynamicMaricopaMap));


